import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useHistory } from 'react-router-dom'
import ImageCrop from '../UiElements/ImageCrop'
import BetaVersionBadge from '../UiElements/BetaVersionBadge'
import MixedContentPreview from '../UiElements/MixedContentPreview'
import AskModal from './AskModal'
import ExplainSessionView, { ExplainFinishModal, ExplainPlayerControls } from './ExplainSessionView'
import ExplainAskModal from './ExplainAskModal'
import ExplainSessionReview from './ExplainSessionReview'
import {
  EXPLAIN_SESSION_PAGE_SIZE,
  explainSessionQuestionText,
  explainStatusLabel,
  explainTimestampMillis,
  fetchExplainChat,
  fetchExplainSessions,
  formatExplainDate,
  isExplainSessionResumable,
  normalizeExplainStep
} from '../../helpers/explainSessions'
import { acceptExplainVisual } from '../../helpers/explainVisuals'
import {
  cancelSpeech,
  isSpeechSupported,
  resolveSpeechVoiceUri,
  speak,
  subscribeSpeechVoices
} from '../../helpers/speechNarrator'
import {
  EXPLAIN_SOLUTION_VIEW_WORKSHEET,
  persistExplainNarrationEnabled,
  persistExplainNarrationVoiceUri,
  persistExplainSolutionView,
  readExplainNarrationEnabled,
  readExplainNarrationVoiceUri,
  readExplainSolutionView
} from '../../helpers/explainNarrationPrefs'
import { compressImage } from '../../helpers/imageCompression'
import { getChatCompletion } from '../../helpers/openaiApiHandler'
import { getVideoRecommendations } from '../../helpers/QuestionParentFeedback'
import { db, functions } from '../../helpers/firebase'
import { httpsCallable } from 'firebase/functions'
import { collection, addDoc, serverTimestamp, doc, updateDoc, getDoc, onSnapshot, query, orderBy, getDocs, where } from 'firebase/firestore'
import StudentReportModal from './StudentReportModal'
import StudentUsernameModal from './StudentUsernameModal'
import HelperQuotaModal from './HelperQuotaModal'
import ParentInvitePanel from './ParentInvitePanel'
import StudyHelperDemo from './StudyHelperDemo'
import ReportExerciseSheet from './ReportExerciseSheet'
import {
  buildExerciseContext,
  buildPageInfo,
  captureExerciseJpeg,
  captureViewportJpeg,
  createReportId,
  preparePageForCapture,
  submitStudentReport
} from '../../helpers/studentReport'
import SortIcon from '@material-ui/icons/Sort'
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import BlockIcon from '@material-ui/icons/Block'
import Brightness4Icon from '@material-ui/icons/Brightness4'
import Brightness7Icon from '@material-ui/icons/Brightness7'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import ListIcon from '@material-ui/icons/List'
import EditIcon from '@material-ui/icons/Edit'
import HistoryIcon from '@material-ui/icons/History'
import CelebrationOverlay from '../UiElements/CelebrationOverlay'
import { isStudentRole } from '../../constants/roles'
import { shouldAutoOpenStudentTutorial } from '../../helpers/studentTutorialOffer'
import './StudyRoom.css'
import './ParentExplainPanel.css'
import './StudyRoomViewWork.css'
import '../../styles/dynamic-background.css'

const ThemeToggleButton = () => {
  const { theme, toggleTheme } = useTheme()
  return (
    <button
      className="qn-theme-toggle"
      onClick={toggleTheme}
      title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
    >
      {theme === 'light' ? <Brightness4Icon style={{ fontSize: 20 }} /> : <Brightness7Icon style={{ fontSize: 20 }} />}
    </button>
  )
}

const EXPLAIN_SESSIONS_LOAD_ERROR =
  'We could not load your explain sessions right now. Please try again.'
const EXPLAIN_CHAT_LOAD_ERROR =
  'We could not load this conversation right now. Please try again.'
const TYPED_PROBLEM_MAX_CHARS = 4000
const TYPED_PROBLEM_EMPTY_MESSAGE = 'Type a math problem first.'
const TYPED_PROBLEM_CONVERT_FALLBACK =
  "We couldn't convert that problem. Please try again."

const PLACEMENT_MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' }
const PLACEMENT_LABELS = { 1: '1st Place', 2: '2nd Place', 3: '3rd Place' }

const GamificationBar = ({ xp, streak, badges, placementRank }) => {
  return (
    <div className="study-room-gamification-bar">
      <div className="study-room-xp-display">
        <span className="study-room-xp-icon">⭐</span>
        <span className="study-room-xp-value">{xp}</span>
        <span className="study-room-xp-label">XP</span>
      </div>
      <div className="study-room-gamification-divider" />
      <div className="study-room-streak-display">
        <span className="study-room-streak-icon">🔥</span>
        <span className="study-room-streak-value">{streak}</span>
        <span className="study-room-streak-label">Day Streak</span>
      </div>
      <div className="study-room-gamification-divider" />
      <div className="study-room-badge-display">
        {badges.map((b, i) => (
          <div key={i} className={`study-room-badge ${b.earned ? 'earned' : 'locked'}`} title={b.label}>
            {b.icon}
          </div>
        ))}
      </div>
      {placementRank && (
        <>
          <div className="study-room-gamification-divider" />
          <div className="study-room-placement-badge" title={`You placed ${PLACEMENT_LABELS[placementRank]} in the XP Competition!`}>
            <span className="study-room-placement-medal">{PLACEMENT_MEDALS[placementRank]}</span>
            <span className="study-room-placement-label">{PLACEMENT_LABELS[placementRank]}</span>
          </div>
        </>
      )}
    </div>
  )
}

const ProgressBar = ({ answered, total }) => {
  const pct = total > 0 ? Math.round((answered / total) * 100) : 0
  return (
    <div className="study-room-progress-bar-container">
      <div className="study-room-progress-bar-track">
        <div className="study-room-progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="study-room-progress-bar-text">{answered}/{total} completed</div>
    </div>
  )
}

const HelperChoiceScreen = ({ onSelectExplain, onSelectCheckAnswer }) => {
  return (
    <div className="study-helper-choice-screen">
      <div className="study-room-bird-mascot">
        <img src="/favicon.png" alt="Quillnote bird" />
      </div>
      <h2 className="study-helper-choice-title">What do you need help with?</h2>
      <p className="study-helper-choice-subtitle">
        Choose how you'd like to work through this problem
      </p>
      <div className="study-helper-choice-buttons">
        <button
          type="button"
          className="study-helper-choice-btn explain"
          onClick={onSelectExplain}
        >
          <span className="study-helper-choice-btn-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 18h.01M9 9a3 3 0 1 1 4 2.83c-.7.27-1 .9-1 1.67v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </span>
          <span className="study-helper-choice-btn-text">
            <span className="study-helper-choice-btn-title">Explain this math problem</span>
            <span className="study-helper-choice-btn-desc">Get a step-by-step walkthrough of a new problem</span>
          </span>
        </button>
        <button
          type="button"
          className="study-helper-choice-btn check-answer"
          onClick={onSelectCheckAnswer}
        >
          <span className="study-helper-choice-btn-icon" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            </svg>
          </span>
          <span className="study-helper-choice-btn-text">
            <span className="study-helper-choice-btn-title">Help me with my answer</span>
            <span className="study-helper-choice-btn-desc">Upload a question and check if your answer is correct</span>
          </span>
        </button>
      </div>
    </div>
  )
}

const HelperProblemEntryZone = ({
  intent,
  isTyping,
  typedProblem,
  typedProblemRef,
  errorMessage,
  disabled,
  onChooseFile,
  onTakePhoto,
  onTypeProblem,
  onTypedProblemChange,
  onSubmitTyped,
  onCancelTyped,
  onBack
}) => {
  const isExplain = intent === 'explain'
  const subtitle = isTyping
    ? "We'll convert the math notation and keep your wording the same."
    : isExplain
      ? "Take a photo, select an image, or type the problem and we'll walk you through it step by step — this won't grade an answer"
      : 'Take a photo, select an image, or type the problem to get started'
  const canSubmit = Boolean(typedProblem && typedProblem.trim()) && !disabled

  const handleTypedKeyDown = (event) => {
    if (event.key !== 'Enter' || (!event.ctrlKey && !event.metaKey)) return
    event.preventDefault()
    if (canSubmit) onSubmitTyped()
  }

  return (
    <div className={`study-helper-desktop-upload-zone${isTyping ? ' typing' : ''}`}>
      <button
        type="button"
        className="study-helper-desktop-back-to-choice"
        onClick={isTyping ? onCancelTyped : onBack}
        disabled={disabled}
      >
        ← Back
      </button>
      {!isTyping && (
        <div className="study-helper-desktop-upload-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16V4M12 4L8 8M12 4L16 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 15V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
      <div className="study-room-bird-mascot">
        <img src="/favicon.png" alt="Quillnote bird" />
      </div>
      <h2 className="study-helper-desktop-upload-title">What math problem do you need help with?</h2>
      <p className="study-helper-desktop-upload-subtitle">
        {subtitle}
      </p>
      {errorMessage && (
        <div className="study-helper-desktop-upload-error" role="alert">
          {errorMessage}
        </div>
      )}
      {isTyping ? (
        <form
          className="study-helper-typed-problem-form"
          onSubmit={(event) => {
            event.preventDefault()
            if (canSubmit) onSubmitTyped()
          }}
        >
          <label htmlFor="helper-typed-problem" className="study-helper-sr-only">
            Math problem
          </label>
          <textarea
            id="helper-typed-problem"
            ref={typedProblemRef}
            className="study-helper-typed-problem-input"
            value={typedProblem}
            onChange={(event) => onTypedProblemChange(event.target.value)}
            onKeyDown={handleTypedKeyDown}
            maxLength={TYPED_PROBLEM_MAX_CHARS}
            rows={7}
            disabled={disabled}
            aria-invalid={Boolean(errorMessage)}
            aria-describedby="helper-typed-problem-count"
            placeholder="Type the problem the way it appears in your book, like 2x + 4 = 10 or a word problem."
            autoComplete="off"
            spellCheck="true"
          />
          <div className="study-helper-typed-problem-meta">
            <span id="helper-typed-problem-count">
              {typedProblem.length} / {TYPED_PROBLEM_MAX_CHARS}
            </span>
            <span className="study-helper-typed-problem-hint">Ctrl+Enter to submit</span>
          </div>
          <div className="study-helper-typed-problem-actions">
            <button
              type="button"
              className="study-helper-typed-problem-cancel"
              onClick={onCancelTyped}
              disabled={disabled}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="study-helper-desktop-upload-btn"
              disabled={!canSubmit}
            >
              Submit
            </button>
          </div>
        </form>
      ) : (
        <div className="study-helper-desktop-upload-buttons">
          <button
            type="button"
            className="study-helper-desktop-upload-btn"
            onClick={onChooseFile}
            disabled={disabled}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 16L4 17C4 18.6569 5.34315 20 7 20L17 20C18.6569 20 20 18.6569 20 17L20 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 8L12 4M12 4L8 8M12 4L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Choose File
          </button>
          <button
            type="button"
            className="study-helper-desktop-camera-btn"
            onClick={onTakePhoto}
            disabled={disabled}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Take Photo
          </button>
          <button
            type="button"
            className="study-helper-desktop-type-btn"
            onClick={onTypeProblem}
            disabled={disabled}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 20h4L18.5 9.5a2.121 2.121 0 0 0-3-3L5 17v3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.5 6.5l3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Type Problem
          </button>
        </div>
      )}
    </div>
  )
}

const ExplainAnalyzingScreen = ({ processingLabel }) => (
  <div className="explain-analyzing-screen" role="status" aria-live="polite">
    <div className="study-room-bird-mascot thinking">
      <img src="/favicon.png" alt="" />
    </div>
    <h2 className="explain-analyzing-title">Working on your problem</h2>
    <p className="explain-analyzing-label">{processingLabel}</p>
    <div className="study-room-processing-spinner" aria-hidden="true" />
  </div>
)

const CameraCaptureModal = ({
  cameraError,
  cameraStream,
  videoRef,
  canvasRef,
  onClose,
  onRetry,
  onCapture
}) => (
  <div className="study-helper-camera-modal-overlay">
    <div
      className="study-helper-camera-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="study-helper-camera-title"
    >
      <div className="study-helper-camera-modal-header">
        <h3 id="study-helper-camera-title">Take a Photo</h3>
        <button
          type="button"
          className="study-helper-camera-close-btn"
          onClick={onClose}
          aria-label="Close camera"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      <div className="study-helper-camera-modal-body">
        {cameraError ? (
          <div className="study-helper-camera-error">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M15 9L9 15M9 9L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <p>{cameraError}</p>
            <button type="button" className="study-helper-camera-retry-btn" onClick={onRetry}>
              Try Again
            </button>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="study-helper-camera-video"
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            {!cameraStream && (
              <p className="study-helper-camera-starting">Starting camera…</p>
            )}
          </>
        )}
      </div>
      {!cameraError && cameraStream && (
        <div className="study-helper-camera-modal-footer">
          <button type="button" className="study-helper-camera-capture-btn" onClick={onCapture}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <circle cx="12" cy="12" r="7" fill="currentColor"/>
            </svg>
            Capture
          </button>
        </div>
      )}
    </div>
  </div>
)

const generateParentSummaries = async (questionLatex, allAttempts, quota = null) => {
  try {
    const messages = [
      {
        role: "system",
        content: `You are an AI assistant tasked with summarizing a student's performance on a math question for their parent. Based on the provided question and the student's attempts, generate two summaries:
1. "answerSummary": A 4-6 sentence summary detailing what the student did across their attempts, highlighting their approach, any misconceptions, and their final outcome.
2. "shortSummary": A condensed 1-2 sentence version of the "answerSummary".

Focus on objective observations of the student's work rather than subjective praise.
Example for answerSummary:
The student was presented with a problem involving [topic of question]. In their first attempt, they correctly identified the initial steps but made an error in [specific error]. For their second attempt, they revisited the problem and managed to correct the previous mistake, leading to a correct solution. The student demonstrated understanding of [concept] but struggled initially with [another concept]. Their final answer was correct after two attempts.

Example for shortSummary:
The student initially made a small error but corrected it on their second attempt, ultimately solving the problem correctly.`
      },
      {
        role: "user",
        content: `Question: ${questionLatex || ''}
Student's Attempts:
${allAttempts.map((attempt, index) => `Attempt ${index + 1}:
  Answer: ${attempt.answer.latex || attempt.answer.text}
  Feedback: ${attempt.feedback.message}`).join('\n\n')}

Please generate the "answerSummary" and "shortSummary" for the parent.`
      }
    ]

    const aiResponse = await getChatCompletion(messages, 'gpt-4', 0.1, quota)

    const lines = aiResponse.split('\n');
    let answerSummary = '';
    let shortSummary = '';
    let currentSummary = null;

    for (const line of lines) {
      if (line.trim().startsWith('answerSummary:')) {
        currentSummary = 'answer';
        answerSummary += line.replace('answerSummary:', '').trim() + ' ';
      } else if (line.trim().startsWith('shortSummary:')) {
        currentSummary = 'short';
        shortSummary += line.replace('shortSummary:', '').trim() + ' ';
      } else if (currentSummary) {
        if (currentSummary === 'answer') {
          answerSummary += line.trim() + ' ';
        } else if (currentSummary === 'short') {
          shortSummary += line.trim() + ' ';
        }
      }
    }

    return {
      answerSummary: answerSummary.trim(),
      shortSummary: shortSummary.trim()
    };

  } catch (error) {
    console.error('Error generating parent summaries:', error)
    return {
      answerSummary: 'Could not generate summary.',
      shortSummary: 'Could not generate summary.'
    }
  }
}

const readFileAsBase64 = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(reader.result.split(',')[1])
  reader.onerror = reject
  reader.readAsDataURL(blob)
})

// The explain callables raise HttpsErrors whose messages are already written
// for a student, so those are surfaced as-is. Transport failures and locally
// thrown errors fall back to the caller's copy.
const explainErrorMessage = (error, fallback) => {
  const rawCode = typeof error?.code === 'string' ? error.code : ''
  const code = rawCode.replace(/^functions\//, '')

  if (code === 'unauthenticated') {
    return 'Your session expired. Please sign in again.'
  }
  if (code === 'unavailable' || code === 'deadline-exceeded' || code === 'cancelled') {
    return 'We could not reach Quillnote. Check your connection and try again.'
  }

  let message = typeof error?.message === 'string' ? error.message.trim() : ''
  if (code && message) {
    const prefixes = [
      `functions/${code}: `,
      `${code}: `,
      `${String(code).toUpperCase().replace(/-/g, '_')}: `
    ]
    for (const prefix of prefixes) {
      if (message.toLowerCase().startsWith(prefix.toLowerCase())) {
        message = message.slice(prefix.length).trim()
        break
      }
    }
    if (message) return message
  }
  return fallback
}

const helperQuotaDetails = (error) => {
  const details = error && (error.details || error.customData)
  if (details && details.reason === 'helper_quota_exceeded') return details
  return null
}

const mintHelperAttemptId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `attempt-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`
}

// The Study Helper serves two callers with one implementation (Phase 4):
//
//   * a signed-in student working on their own account, and
//   * a parent in Student View working as one of their children, which is the
//     only interactive path for a credential-light child who has no login.
//
// Everything below reads and writes `studentUid`, never the signed-in uid, so
// there is no second copy of this screen to keep in step. In Student View the
// callables additionally receive `studentId`, which the server honours only
// while its own Student View session names that child.
const StudyRoom = ({
  studentView = false,
  actingStudentId = null,
  actingStudentProfile = null,
  onExitStudentView = null
}) => {
  const { currentUser, userProfile, logout, refreshUserProfile } = useAuth()
  const { theme } = useTheme()
  const history = useHistory()
  const logoSrc = theme === 'dark' ? '/logo-white.png' : '/logo-blue.png'

  // Whose work this session belongs to, and whose profile drives the parental
  // controls (the AI chat helper toggle) and the name in the header.
  const studentUid = studentView ? actingStudentId : currentUser?.uid
  const studentProfile = studentView ? actingStudentProfile : userProfile
  const studentFirstName = studentProfile?.firstName || 'Student'
  const [question, setQuestion] = useState(null)
  const [questionMetadata, setQuestionMetadata] = useState(null)
  const [answer, setAnswer] = useState(null)
  const [processingImage, setProcessingImage] = useState(false)
  const [showImageCrop, setShowImageCrop] = useState(false)
  const [imageToCrop, setImageToCrop] = useState(null)
  const [checkingAnswer, setCheckingAnswer] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [attemptNumber, setAttemptNumber] = useState(1)
  const [allAttempts, setAllAttempts] = useState([])
  const [showAskModal, setShowAskModal] = useState(false)
  const [aiChatHelperEnabled, setAiChatHelperEnabled] = useState(true)
  const [reportsEnabled, setReportsEnabled] = useState(true)
  const [reportScreencapEnabled, setReportScreencapEnabled] = useState(true)
  const [showReportModal, setShowReportModal] = useState(false)
  const [reportCategory, setReportCategory] = useState('')
  const [reportMessage, setReportMessage] = useState('')
  const [reportIncludeScreenshot, setReportIncludeScreenshot] = useState(false)
  const [reportSubmitting, setReportSubmitting] = useState(false)
  const [reportError, setReportError] = useState(null)
  const [reportSuccess, setReportSuccess] = useState(false)
  const [reportFallbackNeeded, setReportFallbackNeeded] = useState(false)
  const reportIdRef = useRef(null)
  const [showConfetti, setShowConfetti] = useState(false)
  const [isCelebrating, setIsCelebrating] = useState(false)
  const [showXpPop, setShowXpPop] = useState(false)
  const [xpEarned, setXpEarned] = useState(0)
  const [xp, setXp] = useState(0)
  const [streak, setStreak] = useState(0)
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0)
  const [questionsAnswered, setQuestionsAnswered] = useState(0)
  const defaultBadges = [
    { icon: '🎯', label: 'First perfect score', earned: false },
    { icon: '🔥', label: '5 in a row!', earned: false },
    { icon: '📚', label: '10 questions answered', earned: false },
    { icon: '🏆', label: '50 XP earned', earned: false }
  ]
  const [badges, setBadges] = useState(defaultBadges)
  const [lastStudy, setLastStudy] = useState(null)
  const lastStudyRef = useRef(null)
  const [gamificationLoaded, setGamificationLoaded] = useState(false)
  const gamificationSaveTimeoutRef = useRef(null)
  const [analyzingQuestion, setAnalyzingQuestion] = useState(false)
  const [showCameraModal, setShowCameraModal] = useState(false)
  const [cameraStream, setCameraStream] = useState(null)
  const [cameraError, setCameraError] = useState(null)
  const fileInputRef = useRef(null)
  const typedProblemRef = useRef(null)
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const cameraStreamRef = useRef(null)
  const cameraAbortRef = useRef(false)
  const autoSaveDocIdRef = useRef(null)
  const typedSubmitLockRef = useRef(false)
  
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768
    }
    return true
  })
  const [showMobileMoreMenu, setShowMobileMoreMenu] = useState(false)
  const [selectedMenuItem, setSelectedMenuItem] = useState('helper')
  // null = choice screen, 'check-answer' = existing grade pipeline, 'explain' = step-by-step teaching flow
  const [helperIntent, setHelperIntent] = useState(null)
  const [isTypingProblem, setIsTypingProblem] = useState(false)
  const [typedProblem, setTypedProblem] = useState('')
  const [helperEntryError, setHelperEntryError] = useState(null)
  const [helperQuota, setHelperQuota] = useState(null)
  const [showHelperQuotaModal, setShowHelperQuotaModal] = useState(false)
  const [showParentInvite, setShowParentInvite] = useState(false)
  const [tutorialManual, setTutorialManual] = useState(false)
  const [tutorialOffer, setTutorialOffer] = useState(false)
  const [tutorialDismissed, setTutorialDismissed] = useState(false)
  const helperAttemptIdRef = useRef('')
  // The 5th session commits when it starts. The limit modal waits until the
  // student finishes that session, so it does not cover the lesson.
  const quotaModalAfterSessionRef = useRef(false)

  // Explain workflow. `explainSession` holds the server-owned lesson (steps,
  // final answer) exactly as analyzeExplainQuestion returned it and is never
  // mutated; the playback state lives below. Only the step index goes back to
  // Firestore (persistExplainPlayback) — narration and the solution layout are
  // device-local, so mute, voice, and cards-versus-worksheet go to localStorage
  // instead (helpers/explainNarrationPrefs).
  const [explainSession, setExplainSession] = useState(null)
  const [explainStepIndex, setExplainStepIndex] = useState(0)
  // Narration starts muted; the student opts in, and that opt-in is remembered
  // per student on this device (see helpers/explainNarrationPrefs). The ref
  // mirrors the state so a Next/Previous press in the same tick as a mute
  // still observes the mute.
  const [explainTtsEnabled, setExplainTtsEnabled] = useState(false)
  const explainTtsEnabledRef = useRef(false)
  // Step cards until this student has chosen the worksheet. Scoped to the
  // student being taught, so a sibling in Student View does not inherit it.
  const [explainSolutionView, setExplainSolutionView] = useState(() => readExplainSolutionView(studentUid))
  // Durable tutor conversation for the open explain session. Seeded into the
  // Ask modal with the current step's explanation; never written into the
  // step panel itself.
  const [explainChatMessages, setExplainChatMessages] = useState([])
  const explainChatRequestRef = useRef(0)
  const [explainError, setExplainError] = useState(null)
  const [explainStage, setExplainStage] = useState(null)
  const [showExplainAskModal, setShowExplainAskModal] = useState(false)
  const [explainFinishing, setExplainFinishing] = useState(false)
  const [explainRefreshing, setExplainRefreshing] = useState(false)
  const explainRefreshLockRef = useRef(false)
  // The uploaded problem photo, kept only for this visit so "Try a different
  // solution" can show Claude the same page. It is never written to Firestore
  // or to the session object. A typed problem, a failed upload, and a lesson
  // reopened from View Work have no photo.
  const explainProblemImageRef = useRef('')
  const [explainCompletion, setExplainCompletion] = useState(null)
  // After the first Finish, the student can hide the celebration modal to
  // scrub the lesson. A later Finish only unhides it — XP and confetti stay
  // with the original completion.
  const [explainFinishModalHidden, setExplainFinishModalHidden] = useState(false)
  const [isNarrating, setIsNarrating] = useState(false)
  const speechSupported = useMemo(() => isSpeechSupported(), [])
  const [explainTtsVoices, setExplainTtsVoices] = useState([])
  const [explainTtsVoiceUri, setExplainTtsVoiceUri] = useState('')
  const explainTtsVoiceUriRef = useRef('')
  const [studyQuestions, setStudyQuestions] = useState([])
  const [loadingQuestions, setLoadingQuestions] = useState(false)
  // My Work merges check-answer history and explain sessions into one list.
  // Check-answer questions are read in full; explain sessions are paged the
  // same way the parent's Explain panel pages them.
  const [explainSessionsList, setExplainSessionsList] = useState([])
  const [explainSessionsCursor, setExplainSessionsCursor] = useState(null)
  const [explainSessionsHasMore, setExplainSessionsHasMore] = useState(false)
  const [loadingExplainSessions, setLoadingExplainSessions] = useState(false)
  const [loadingMoreExplainSessions, setLoadingMoreExplainSessions] = useState(false)
  const [explainSessionsLoadError, setExplainSessionsLoadError] = useState(null)
  const [selectedViewWorkQuestion, setSelectedViewWorkQuestion] = useState(null)
  const [viewWorkChat, setViewWorkChat] = useState([])
  const [viewWorkChatState, setViewWorkChatState] = useState('idle')
  // Guards against a slow chat fetch for a previously opened explain session
  // landing after the student has already selected a different item.
  const viewWorkChatRequestRef = useRef(0)
  const [showViewWorkSortDropdown, setShowViewWorkSortDropdown] = useState(false)
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState('desc')
  const [chatConversation, setChatConversation] = useState(null)
  const [pendingStudyQuestion, setPendingStudyQuestion] = useState(null)
  const [availableQuestions, setAvailableQuestions] = useState([])
  const [loadingAvailableQuestions, setLoadingAvailableQuestions] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState(null)
  const [selectedQuestionsGroupKey, setSelectedQuestionsGroupKey] = useState(null)

  const [competitionData, setCompetitionData] = useState(null)
  const [choosingPrize, setChoosingPrize] = useState(false)
  const [prizeChooseError, setPrizeChooseError] = useState(null)
  const [prizeCountdown, setPrizeCountdown] = useState('')

  // Sent with every callable that stores work for this student. Empty for a
  // student on their own account: the server then simply uses the caller's uid.
  const actingStudentPayload = useMemo(
    () => (studentView && actingStudentId ? { studentId: actingStudentId } : {}),
    [studentView, actingStudentId]
  )

  const openHelperQuotaModal = useCallback((error) => {
    if (!helperQuotaDetails(error)) return false
    quotaModalAfterSessionRef.current = false
    setShowHelperQuotaModal(true)
    setHelperQuota((current) => ({
      limited: true,
      used: 5,
      limit: 5,
      resetsAt: helperQuotaDetails(error).resetsAt || current?.resetsAt || null
    }))
    return true
  }, [])

  const openDeferredQuotaModal = useCallback(() => {
    if (!quotaModalAfterSessionRef.current) return
    quotaModalAfterSessionRef.current = false
    setShowHelperQuotaModal(true)
  }, [])

  const applyHelperQuota = useCallback((data) => {
    const quota = data && data.quota
    if (!quota || quota.limited !== true) return
    setHelperQuota({
      limited: true,
      used: Number(quota.used) || 0,
      limit: 5,
      resetsAt: quota.resetsAt || null
    })
    if (quota.justFilled) quotaModalAfterSessionRef.current = true
  }, [])

  const helperQuotaSource = useCallback(() => ({
    ...actingStudentPayload,
    questionHash: questionMetadata?.questionHash || '',
    helperAttemptId: helperAttemptIdRef.current || ''
  }), [actingStudentPayload, questionMetadata])

  useEffect(() => {
    if (studentView || selectedMenuItem !== 'helper') return undefined
    let cancelled = false
    const loadQuota = async () => {
      try {
        const getHelperQuota = httpsCallable(functions, 'getHelperQuota')
        const result = await getHelperQuota(actingStudentPayload)
        if (!cancelled) setHelperQuota(result.data || null)
      } catch (error) {
        if (!cancelled) setHelperQuota(null)
      }
    }
    loadQuota()
    return () => {
      cancelled = true
    }
  }, [studentView, selectedMenuItem, actingStudentPayload, userProfile?.parentId])

  const usernameModalOpen = !studentView && userProfile?.pendingStudentUsername === true
  const tutorialAutoOpen = shouldAutoOpenStudentTutorial({
    pendingStudentTutorial: userProfile?.pendingStudentTutorial === true,
    usernameModalOpen,
    quotaModalOpen: showHelperQuotaModal,
    invitePanelOpen: showParentInvite
  })

  useEffect(() => {
    if (tutorialDismissed || studentView) return
    if (tutorialAutoOpen) setTutorialOffer(true)
  }, [tutorialAutoOpen, tutorialDismissed, studentView])

  const persistStudentTutorial = useCallback(async () => {
    if (studentView || !currentUser?.uid || !isStudentRole(userProfile?.role)) return
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        studyHelperTutorialCompleted: true,
        pendingStudentTutorial: false
      })
      await refreshUserProfile()
    } catch (error) {
      console.error('Error saving the Study Helper tutorial:', error)
    }
  }, [studentView, currentUser, userProfile?.role, refreshUserProfile])

  const closeStudentTutorial = useCallback(async () => {
    setTutorialManual(false)
    setTutorialOffer(false)
    setTutorialDismissed(true)
    await persistStudentTutorial()
  }, [persistStudentTutorial])

  const showStudentTutorial = tutorialManual || tutorialOffer

  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!showCameraModal || cameraError || !cameraStream) return undefined
    const video = videoRef.current
    if (!video) return undefined
    video.srcObject = cameraStream
    const playPromise = video.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {})
    }
    return () => {
      video.srcObject = null
    }
  }, [showCameraModal, cameraStream, cameraError])

  useEffect(() => {
    return () => {
      cameraAbortRef.current = true
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop())
        cameraStreamRef.current = null
      }
    }
  }, [])

  // The role gate applies to the signed-in account, not to the profile being
  // worked on: in Student View the caller is a parent by definition, and the
  // parent dashboard has already authorized them for this child.
  useEffect(() => {
    if (studentView) {
      setAiChatHelperEnabled(actingStudentProfile?.aiChatHelperEnabled !== false)
      return
    }
    if (!userProfile) return
    if (!isStudentRole(userProfile.role)) {
      history.replace('/')
      return
    }
    setAiChatHelperEnabled(userProfile.aiChatHelperEnabled !== false)
  }, [studentView, actingStudentProfile, userProfile, history])

  // Parental report controls live on the student profile. Absent means on, so
  // a parent who has never touched the setting leaves reporting available.
  useEffect(() => {
    if (!studentUid) return undefined
    return onSnapshot(doc(db, 'users', studentUid), (snapshot) => {
      const data = snapshot.data() || {}
      setReportsEnabled(data.reportsEnabled !== false)
      setReportScreencapEnabled(data.reportScreencapEnabled !== false)
      if (!studentView && data.parentId && !userProfile?.parentId) {
        refreshUserProfile()
      }
    }, () => {
      setReportsEnabled(true)
      setReportScreencapEnabled(true)
    })
  }, [studentUid, studentView, userProfile?.parentId, refreshUserProfile])

  useEffect(() => {
    if (selectedMenuItem === 'view-work' && studentUid) {
      setSelectedViewWorkQuestion(null)
      loadStudyQuestions()
      loadExplainSessionsFirstPage()
    }
  }, [selectedMenuItem, studentUid])

  // Once both sources have settled, default to whichever item is newest
  // overall so switching into My Work always opens on the latest activity.
  useEffect(() => {
    if (selectedViewWorkQuestion) return
    if (loadingQuestions || loadingExplainSessions) return

    const topCheckAnswer = studyQuestions[0]
      ? { ...studyQuestions[0], workItemType: 'check-answer' }
      : null
    const topExplain = explainSessionsList[0]
      ? { ...explainSessionsList[0], workItemType: 'explain' }
      : null
    if (!topCheckAnswer && !topExplain) return

    const initial = topExplain && (!topCheckAnswer ||
      getWorkItemMillis(topExplain) > getWorkItemMillis(topCheckAnswer))
      ? topExplain
      : topCheckAnswer

    setSelectedViewWorkQuestion(initial)
    if (initial.workItemType === 'explain') {
      loadViewWorkExplainChat(initial)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyQuestions, explainSessionsList, loadingQuestions, loadingExplainSessions])

  useEffect(() => {
    if (selectedMenuItem === 'questions' && studentUid) {
      loadAvailableQuestions()
    }
  }, [selectedMenuItem, studentUid])

  useEffect(() => {
    if (selectedMenuItem !== 'helper' && !question && !explainSession) {
      setHelperIntent(null)
      setIsTypingProblem(false)
      setTypedProblem('')
      setHelperEntryError(null)
    }
  }, [selectedMenuItem, question, explainSession])

  useEffect(() => {
    if (!isTypingProblem) return undefined
    const node = typedProblemRef.current
    if (node) node.focus()
    return undefined
  }, [isTypingProblem])

  const applyGamificationSnapshot = useCallback((gamification) => {
    if (!gamification) return
    if (gamification.xp != null) setXp(gamification.xp)
    if (gamification.streak != null) setStreak(gamification.streak)
    if (gamification.questionsAnswered != null) setQuestionsAnswered(gamification.questionsAnswered)
    if (Array.isArray(gamification.badges)) {
      setBadges(prev => prev.map((badge, i) => gamification.badges[i] ? { ...badge, earned: gamification.badges[i].earned } : badge))
    }
    if (gamification.lastStudy) {
      setLastStudy(gamification.lastStudy)
      lastStudyRef.current = gamification.lastStudy
    }
  }, [])

  // Cloud Functions award XP with field-path writes the client never sees.
  // Re-reading after one of those runs is what keeps the local copy from
  // being stale enough to undo the award on its next debounced save.
  const refreshGamificationFromServer = useCallback(async () => {
    if (!studentUid) return
    try {
      const userDoc = await getDoc(doc(db, 'users', studentUid))
      if (userDoc.exists()) {
        applyGamificationSnapshot(userDoc.data().gamification || {})
      }
    } catch (e) {
      console.error('Error refreshing gamification from Firestore:', e)
    }
  }, [studentUid, applyGamificationSnapshot])

  useEffect(() => {
    if (!studentUid) return
    const loadGamification = async () => {
      try {
        const userDocRef = doc(db, 'users', studentUid)
        const userDoc = await getDoc(userDocRef)
        if (userDoc.exists()) {
          applyGamificationSnapshot(userDoc.data().gamification || {})
        }
      } catch (e) {
        console.error('Error loading gamification from Firestore:', e)
      } finally {
        setGamificationLoaded(true)
      }
    }
    loadGamification()
  }, [studentUid, applyGamificationSnapshot])

  useEffect(() => {
    if (!studentUid || !gamificationLoaded) return
    clearTimeout(gamificationSaveTimeoutRef.current)
    // completeExplainSession owns gamification while it runs. Cancelling the
    // pending save here (and not scheduling a new one) stops a pre-award
    // snapshot of xp/streak from landing on top of the XP it just granted.
    if (explainFinishing) return
    gamificationSaveTimeoutRef.current = setTimeout(async () => {
      try {
        const userDocRef = doc(db, 'users', studentUid)
        // Field paths rather than a whole `gamification` object: a full-object
        // write erases server-owned keys such as explainSessionsCompleted and
        // clobbers any concurrent Cloud Function update.
        const gamificationUpdate = {
          'gamification.xp': xp,
          'gamification.streak': streak,
          'gamification.questionsAnswered': questionsAnswered,
          'gamification.badges': badges
        }
        if (lastStudy) {
          gamificationUpdate['gamification.lastStudy'] = lastStudy
        }
        await updateDoc(userDocRef, gamificationUpdate)
      } catch (e) {
        console.error('Error saving gamification to Firestore:', e)
      }
    }, 1500)
  }, [xp, streak, questionsAnswered, badges, lastStudy, studentUid, gamificationLoaded, explainFinishing])

  // Prize selection is tied to the enrolled student's own sign-in: choosePrize
  // authorizes the caller as the winner, so it is not something a parent can do
  // from Student View. XP still accrues to the student either way, and a parent
  // picks prizes from their own dashboard, so Student View simply omits the
  // panel rather than showing a control that would fail.
  useEffect(() => {
    if (studentView) return
    if (!currentUser || !userProfile) return
    if (!isStudentRole(userProfile.role)) return

    const loadCompetitionData = async () => {
      try {
        const entrySnap = await getDoc(doc(db, 'competitionEntries', currentUser.uid))
        if (entrySnap.exists()) {
          const compSnap = await getDoc(doc(db, 'competition', 'spring2026'))
          if (compSnap.exists()) {
            setCompetitionData(compSnap.data())
          }
        }
      } catch (e) {
        console.error('Error loading competition data:', e)
      }
    }
    loadCompetitionData()
  }, [studentView, currentUser, userProfile])

  const triggerStreak = useCallback(() => {
    const today = new Date().toDateString()
    const last = lastStudyRef.current
    if (last !== today) {
      const yesterday = new Date(Date.now() - 86400000).toDateString()
      if (last === yesterday) {
        setStreak(s => s + 1)
      } else {
        setStreak(1)
      }
      setLastStudy(today)
      lastStudyRef.current = today
    }
  }, [])

  const triggerCelebration = useCallback((isCorrect, attemptNum) => {
    triggerStreak()
    if (isCorrect || attemptNum >= 3) {
      setQuestionsAnswered(prev => {
        const next = prev + 1
        if (next >= 10) {
          setBadges(b => b.map(badge => badge.label === '10 questions answered' ? { ...badge, earned: true } : badge))
        }
        return next
      })
    }
    if (isCorrect) {
      const points = attemptNum === 1 ? 20 : attemptNum === 2 ? 10 : 5
      setXpEarned(points)
      setShowXpPop(true)
      setShowConfetti(true)
      setIsCelebrating(true)
      setXp(prev => {
        const nextXp = prev + points
        if (nextXp >= 50) {
          setBadges(b => b.map(badge => badge.label === '50 XP earned' ? { ...badge, earned: true } : badge))
        }
        return nextXp
      })
      setConsecutiveCorrect(prev => {
        const next = prev + 1
        if (next >= 5) {
          setBadges(b => b.map(badge => badge.label === '5 in a row!' ? { ...badge, earned: true } : badge))
        }
        return next
      })
      if (attemptNum === 1) {
        setBadges(b => b.map(badge => badge.label === 'First perfect score' ? { ...badge, earned: true } : badge))
      }
      setTimeout(() => setShowConfetti(false), 5000)
      setTimeout(() => setShowXpPop(false), 2000)

    } else {
      setConsecutiveCorrect(0)
    }
  }, [triggerStreak])

  // ==================== Explain workflow ====================

  const explainSteps = explainSession?.steps || []
  const explainExplanationText = explainSteps[explainStepIndex]?.explanation || ''

  const startNarration = useCallback((text) => {
    if (!speechSupported || !text) return
    setIsNarrating(speak(text, {
      voiceUri: explainTtsVoiceUriRef.current,
      onStart: () => setIsNarrating(true),
      onEnd: () => setIsNarrating(false)
    }))
  }, [speechSupported])

  const stopNarration = useCallback(() => {
    cancelSpeech()
    setIsNarrating(false)
  }, [])

  // The single writer for the mute flag: the ref lands synchronously so every
  // narration decision below can be made without waiting for a re-render.
  const applyExplainTtsEnabled = useCallback((enabled) => {
    explainTtsEnabledRef.current = enabled
    setExplainTtsEnabled(enabled)
  }, [])

  const narrateIfUnmuted = useCallback((text) => {
    if (!explainTtsEnabledRef.current) return
    startNarration(text)
  }, [startNarration])

  // Firestore rules accept only these playback fields, and only while the
  // session is open. A rejected write costs the student nothing, so failures
  // are logged rather than surfaced.
  const persistExplainPlayback = useCallback(async (fields) => {
    const sessionId = explainSession?.sessionId
    if (!studentUid || !sessionId || explainCompletion) return
    try {
      await updateDoc(
        doc(db, 'users', studentUid, 'explainSessions', sessionId),
        { ...fields, updatedAt: serverTimestamp() }
      )
    } catch (e) {
      console.error('Error saving explain playback state:', e)
    }
  }, [studentUid, explainSession, explainCompletion])

  const resetExplainState = useCallback(() => {
    cancelSpeech()
    setIsNarrating(false)
    explainChatRequestRef.current += 1
    setExplainSession(null)
    setExplainStepIndex(0)
    // The mute preference outlives the session, so it is deliberately not
    // reset here.
    setExplainChatMessages([])
    setExplainError(null)
    setExplainStage(null)
    setShowExplainAskModal(false)
    setExplainFinishing(false)
    setExplainCompletion(null)
    setExplainFinishModalHidden(false)
    setIsCelebrating(false)
    setIsTypingProblem(false)
    setTypedProblem('')
    setHelperEntryError(null)
    setExplainRefreshing(false)
    explainProblemImageRef.current = ''
  }, [])

  const startExplainFromQuestion = useCallback(async ({
    questionLatex,
    questionText,
    questionMathml = '',
    questionConfidence = 0,
    replaceSessionId = '',
    helperAttemptId = '',
    imageBase64 = ''
  }) => {
    const attemptId = helperAttemptId || mintHelperAttemptId()
    helperAttemptIdRef.current = attemptId
    setExplainStage('planning')
    const analyzeExplainQuestion = httpsCallable(functions, 'analyzeExplainQuestion')
    const payload = {
      ...actingStudentPayload,
      questionLatex,
      questionText,
      questionMathml,
      questionConfidence,
      helperAttemptId: attemptId
    }
    if (replaceSessionId) {
      payload.replaceSessionId = replaceSessionId
    }
    if (typeof imageBase64 === 'string' && imageBase64) {
      payload.imageBase64 = imageBase64
    }
    const analysis = await analyzeExplainQuestion(payload)
    applyHelperQuota(analysis.data)

    const data = analysis.data
    if (!data || !data.success || !Array.isArray(data.steps) || data.steps.length === 0) {
      throw new Error('analyzeExplainQuestion returned no steps')
    }

    cancelSpeech()
    setExplainSession({
      sessionId: data.sessionId,
      question: questionLatex || questionText,
      questionLatex: questionLatex || '',
      questionText: questionText || '',
      questionMathml: questionMathml || '',
      questionConfidence: questionConfidence || 0,
      steps: data.steps.map(normalizeExplainStep),
      finalAnswer: data.finalAnswer || '',
      finalVisual: acceptExplainVisual(data.finalVisual),
      mathBranch: data.mathBranch || null,
      topic: data.topic || null,
      difficulty: data.difficulty || null,
      questionHash: data.questionHash || null
    })
    setExplainStepIndex(0)
    // The student's stored mute preference already governs narration and
    // carries across sessions, so a new lesson does not reset it.
    explainChatRequestRef.current += 1
    setExplainChatMessages([])
    setExplainCompletion(null)
    setExplainFinishModalHidden(false)
    setIsTypingProblem(false)
    setTypedProblem('')
    setHelperEntryError(null)
    // With the Read-aloud control gone, narration is automatic: a student who
    // left the workflow unmuted hears step 1 without having to press Next.
    narrateIfUnmuted(data.steps[0].explanation)
  }, [actingStudentPayload, narrateIfUnmuted, applyHelperQuota])

  const startExplainSession = useCallback(async (imageBlob) => {
    setExplainError(null)
    setHelperEntryError(null)
    setExplainStage('reading')
    const attemptId = mintHelperAttemptId()
    helperAttemptIdRef.current = attemptId
    try {
      const imageBase64 = await readFileAsBase64(imageBlob)
      explainProblemImageRef.current = ''

      const readExplainProblemPhoto = httpsCallable(functions, 'readExplainProblemPhoto')
      const readingResult = await readExplainProblemPhoto({
        ...actingStudentPayload,
        imageBase64,
        helperAttemptId: attemptId
      })
      const reading = readingResult.data
      if (!reading || !reading.success || typeof reading.hasVisual !== 'boolean') {
        throw new Error('readExplainProblemPhoto returned no reading')
      }

      let questionLatex = ''
      let questionText = ''
      let questionMathml = ''
      let questionConfidence = 0
      let explainImage = ''

      if (reading.hasVisual) {
        questionLatex = reading.latex || reading.text || ''
        questionText = reading.text || reading.latex || ''
        questionConfidence = Number.isFinite(Number(reading.confidence)) ?
          Number(reading.confidence) : 1
        explainImage = imageBase64
      } else {
        const processMathpixOCR = httpsCallable(functions, 'processMathpixOCR')
        const mathpixResult = await processMathpixOCR({
          ...actingStudentPayload,
          imageBase64,
          helperAttemptId: attemptId
        })
        if (!mathpixResult.data || !mathpixResult.data.success) {
          throw new Error('Mathpix OCR failed')
        }
        questionLatex = mathpixResult.data.latex || mathpixResult.data.text || ''
        questionText = mathpixResult.data.text || ''
        questionMathml = mathpixResult.data.mathml || ''
        questionConfidence = mathpixResult.data.confidence || 0
      }

      if (!questionLatex.trim() && !questionText.trim()) {
        setHelperEntryError("We couldn't find a math problem in that image. Try again with a clearer photo.")
        return
      }

      explainProblemImageRef.current = explainImage
      await startExplainFromQuestion({
        questionLatex,
        questionText,
        questionMathml,
        questionConfidence,
        helperAttemptId: attemptId,
        imageBase64: explainImage
      })
    } catch (error) {
      explainProblemImageRef.current = ''
      console.error('Error starting explain session:', error)
      if (openHelperQuotaModal(error)) return
      setHelperEntryError(explainErrorMessage(
        error,
        "We couldn't read that image. Try again with a clearer photo of the problem."
      ))
    } finally {
      setExplainStage(null)
    }
  }, [startExplainFromQuestion, actingStudentPayload, openHelperQuotaModal])

  const handleExplainNext = useCallback(() => {
    if (!explainSession || explainFinishing) return
    const nextIndex = explainStepIndex + 1
    if (nextIndex > explainSession.steps.length - 1) return
    setExplainStepIndex(nextIndex)
    persistExplainPlayback({ currentStepIndex: nextIndex })
    narrateIfUnmuted(explainSession.steps[nextIndex].explanation)
  }, [explainSession, explainStepIndex, explainFinishing, persistExplainPlayback, narrateIfUnmuted])

  const handleExplainPrevious = useCallback(() => {
    if (!explainSession || explainFinishing) return
    const previousIndex = explainStepIndex - 1
    if (previousIndex < 0) return
    setExplainStepIndex(previousIndex)
    persistExplainPlayback({ currentStepIndex: previousIndex })
    narrateIfUnmuted(explainSession.steps[previousIndex].explanation)
  }, [explainSession, explainStepIndex, explainFinishing, persistExplainPlayback, narrateIfUnmuted])

  const handleToggleExplainTts = useCallback(() => {
    const nextEnabled = !explainTtsEnabledRef.current
    // Silence first, so nothing keeps playing while the rest of the toggle
    // settles.
    if (!nextEnabled) stopNarration()
    applyExplainTtsEnabled(nextEnabled)
    persistExplainNarrationEnabled(studentUid, nextEnabled)
    if (nextEnabled) {
      startNarration(explainExplanationText)
    }
  }, [explainExplanationText, studentUid, applyExplainTtsEnabled, startNarration, stopNarration])

  const handleExplainSolutionViewChange = useCallback((view) => {
    setExplainSolutionView(view)
    persistExplainSolutionView(studentUid, view)
  }, [studentUid])

  const handleExplainVoiceChange = useCallback((voiceUri) => {
    if (typeof voiceUri !== 'string' || !voiceUri) return
    explainTtsVoiceUriRef.current = voiceUri
    setExplainTtsVoiceUri(voiceUri)
    persistExplainNarrationVoiceUri(studentUid, voiceUri)
    if (isNarrating && explainTtsEnabledRef.current) {
      startNarration(explainExplanationText)
    }
  }, [isNarrating, explainExplanationText, studentUid, startNarration])

  const handleExplainMessagesAppend = useCallback((newMessages, forSessionId) => {
    if (!Array.isArray(newMessages) || newMessages.length === 0) return
    if (forSessionId && forSessionId !== explainSession?.sessionId) return
    setExplainChatMessages(prev => [...prev, ...newMessages])
  }, [explainSession?.sessionId])

  const handleHideExplainFinishModal = useCallback(() => {
    setExplainFinishModalHidden(true)
    openDeferredQuotaModal()
  }, [openDeferredQuotaModal])

  const handleExplainFinish = useCallback(async () => {
    if (!explainSession || explainFinishing) return
    if (explainCompletion) {
      stopNarration()
      setExplainFinishModalHidden(false)
      return
    }
    stopNarration()
    setExplainError(null)
    setExplainFinishing(true)
    try {
      const completeExplainSession = httpsCallable(functions, 'completeExplainSession')
      const result = await completeExplainSession({
        ...actingStudentPayload,
        sessionId: explainSession.sessionId
      })
      const data = result.data || {}
      const xpAwarded = data.xpAwarded === true
      const xpAmount = Number(data.xpAmount) || 0

      // Pull the server's gamification back down before the debounced save is
      // allowed to run again, so the XP just awarded becomes the local truth.
      await refreshGamificationFromServer()
      setExplainFinishModalHidden(false)
      setExplainCompletion({ xpAwarded, xpAmount })

      if (xpAwarded && xpAmount > 0) {
        setXpEarned(xpAmount)
        setShowXpPop(true)
        setShowConfetti(true)
        setIsCelebrating(true)
        setTimeout(() => setShowConfetti(false), 5000)
        setTimeout(() => setShowXpPop(false), 2000)
      }
    } catch (error) {
      console.error('Error completing explain session:', error)
      setExplainError(explainErrorMessage(
        error,
        "We couldn't finish this session. Please try again."
      ))
    } finally {
      setExplainFinishing(false)
    }
  }, [explainSession, explainFinishing, explainCompletion, stopNarration, refreshGamificationFromServer, actingStudentPayload])

  const handleExplainStartOver = useCallback(async () => {
    const sessionId = explainSession?.sessionId
    const wasCompleted = Boolean(explainCompletion)
    resetExplainState()
    setHelperIntent(null)
    if (wasCompleted) openDeferredQuotaModal()

    // Abandoning is a courtesy flag for the parent dashboard; completion is
    // only ever set by completeExplainSession.
    if (!sessionId || wasCompleted || !studentUid) return
    try {
      await updateDoc(
        doc(db, 'users', studentUid, 'explainSessions', sessionId),
        { status: 'abandoned', updatedAt: serverTimestamp() }
      )
    } catch (e) {
      console.error('Error abandoning explain session:', e)
    }
  }, [explainSession, explainCompletion, studentUid, resetExplainState, openDeferredQuotaModal])

  const handleExplainRefresh = useCallback(async () => {
    if (explainRefreshLockRef.current) return
    if (!explainSession || explainFinishing || explainCompletion || explainRefreshing) return

    const questionLatex = explainSession.questionLatex || ''
    const questionText = explainSession.questionText || explainSession.question || ''
    if (!questionLatex.trim() && !questionText.trim()) return

    explainRefreshLockRef.current = true
    const previousSessionId = explainSession.sessionId
    stopNarration()
    setShowExplainAskModal(false)
    setExplainError(null)
    setExplainRefreshing(true)
    explainChatRequestRef.current += 1

    try {
      await startExplainFromQuestion({
        questionLatex,
        questionText,
        questionMathml: explainSession.questionMathml || '',
        questionConfidence: explainSession.questionConfidence || 0,
        replaceSessionId: previousSessionId,
        imageBase64: explainProblemImageRef.current
      })
    } catch (error) {
      console.error('Error refreshing explain session:', error)
      if (openHelperQuotaModal(error)) return
      setExplainError(explainErrorMessage(
        error,
        "We couldn't build a new explanation. Your current lesson is unchanged. Please try again."
      ))
    } finally {
      setExplainRefreshing(false)
      setExplainStage(null)
      explainRefreshLockRef.current = false
    }
  }, [
    explainSession,
    explainFinishing,
    explainCompletion,
    explainRefreshing,
    stopNarration,
    startExplainFromQuestion,
    openHelperQuotaModal
  ])

  const handleExplainAnother = useCallback(() => {
    resetExplainState()
    setHelperIntent('explain')
    setSelectedMenuItem('helper')
    openDeferredQuotaModal()
  }, [resetExplainState, openDeferredQuotaModal])

  const handleExplainSwitchToCheckAnswer = useCallback(() => {
    resetExplainState()
    setHelperIntent('check-answer')
    setSelectedMenuItem('helper')
    openDeferredQuotaModal()
  }, [resetExplainState, openDeferredQuotaModal])

  // Resume an unfinished session from View Work. Hydrates the same in-memory
  // shape analyzeExplainQuestion produces so the existing Helper path can
  // continue without a second analyze call.
  const resumeExplainFromViewWork = useCallback((session) => {
    if (!isExplainSessionResumable(session)) return

    cancelSpeech()
    setIsNarrating(false)
    explainChatRequestRef.current += 1
    const chatRequestId = explainChatRequestRef.current
    // This session's photo was never stored, so a later refresh must not
    // attach a picture from a different problem still held on this page.
    explainProblemImageRef.current = ''
    setExplainChatMessages([])
    setExplainError(null)
    setExplainStage(null)
    setShowExplainAskModal(false)
    setExplainFinishing(false)
    setExplainCompletion(null)
    setExplainFinishModalHidden(false)
    setIsCelebrating(false)

    const stepIndex = session.currentStepIndex
    // This device's saved preference for this student wins. A session started
    // before narration moved off the session document has no stored
    // preference yet, so its own ttsEnabled flag is honoured as the fallback.
    const ttsEnabled = readExplainNarrationEnabled(studentUid, session.ttsEnabled === true)

    setExplainSession({
      sessionId: session.id,
      question: explainSessionQuestionText(session),
      questionLatex: session.question?.latex || '',
      questionText: session.question?.text || '',
      questionMathml: session.question?.mathml || '',
      questionConfidence: session.question?.confidence || 0,
      steps: session.steps,
      finalAnswer: session.finalAnswer || '',
      finalVisual: session.finalVisual || null,
      mathBranch: session.mathBranch || null,
      topic: session.topic || null,
      difficulty: session.difficulty || null,
      questionHash: session.questionHash || null
    })
    setExplainStepIndex(stepIndex)
    applyExplainTtsEnabled(ttsEnabled)
    setHelperIntent('explain')
    setSelectedMenuItem('helper')

    // If the student had abandoned the session, mark it active again so the
    // parent dashboard does not keep showing "Stopped" while they continue.
    if (session.status === 'abandoned' && studentUid) {
      updateDoc(
        doc(db, 'users', studentUid, 'explainSessions', session.id),
        { status: 'in-progress', updatedAt: serverTimestamp() }
      ).catch((error) => {
        console.error('Error reopening abandoned explain session:', error)
      })
    }

    // The chat fetch below is async, so the ref (not the captured flag) decides:
    // a mute pressed while the reply is loading must still win.
    const narrate = (text) => {
      if (!explainTtsEnabledRef.current || !text) return
      setTimeout(() => {
        if (!explainTtsEnabledRef.current) return
        setIsNarrating(speak(text, {
          voiceUri: explainTtsVoiceUriRef.current,
          onStart: () => setIsNarrating(true),
          onEnd: () => setIsNarrating(false)
        }))
      }, 0)
    }

    narrate(session.steps[stepIndex]?.explanation)

    if (studentUid && session.chatMessageCount > 0) {
      fetchExplainChat(studentUid, session.id)
        .then((messages) => {
          if (chatRequestId !== explainChatRequestRef.current) return
          setExplainChatMessages(messages)
        })
        .catch((error) => {
          console.error('Error restoring explain chat on resume:', error)
        })
    }
  }, [studentUid, applyExplainTtsEnabled])

  // Narration preferences belong to the student being taught, not to the
  // signed-in account: in Student View one parent works as several children on
  // the same browser, and a sibling must never inherit another child's mute.
  useEffect(() => {
    if (!studentUid) return
    applyExplainTtsEnabled(readExplainNarrationEnabled(studentUid))
    setExplainSolutionView(readExplainSolutionView(studentUid))
  }, [studentUid, applyExplainTtsEnabled])

  // Which voices exist is a property of the device; which one wins is the
  // student's saved pick. A saved voice the engine no longer offers falls back
  // rather than leaving the narrator silent.
  useEffect(() => {
    if (!speechSupported) return undefined
    explainTtsVoiceUriRef.current = studentUid ? readExplainNarrationVoiceUri(studentUid) : ''
    return subscribeSpeechVoices((voices) => {
      setExplainTtsVoices(voices)
      const nextUri = resolveSpeechVoiceUri(voices, explainTtsVoiceUriRef.current)
      explainTtsVoiceUriRef.current = nextUri
      setExplainTtsVoiceUri(nextUri)
    })
  }, [speechSupported, studentUid])

  useEffect(() => {
    if (selectedMenuItem !== 'helper') {
      cancelSpeech()
      setIsNarrating(false)
    }
  }, [selectedMenuItem])

  useEffect(() => {
    return () => cancelSpeech()
  }, [])

  const loadStudyQuestions = async () => {
    if (!studentUid) return
    try {
      setLoadingQuestions(true)
      const questionsRef = collection(db, 'users', studentUid, 'studyQuestions')
      const q = query(questionsRef, orderBy('timestamp', 'desc'))
      const questionsSnapshot = await getDocs(q)
      
      const questions = questionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      const allQuestions = [...questions]

      const answeredTextbookKeys = new Set()
      questions.forEach(q => {
        if (q.sourceType === 'textbook' && q.textbookId && q.pageId) {
          const qText = q.question?.text || q.question?.latex || ''
          answeredTextbookKeys.add(`${q.textbookId}_${q.pageId}_${qText}`)
        }
      })

      try {
        const textbooksRef = collection(db, 'textbooks')
        const tbQ = query(textbooksRef, where('studentIds', 'array-contains', studentUid))
        const tbSnapshot = await getDocs(tbQ)

        for (const tbDoc of tbSnapshot.docs) {
          const tbData = tbDoc.data()
          const pagesRef = collection(db, 'textbooks', tbDoc.id, 'pages')
          const pagesQ = query(pagesRef, orderBy('pageNumber', 'asc'))
          const pagesSnapshot = await getDocs(pagesQ)

          pagesSnapshot.docs.forEach(pageDoc => {
            const pageData = pageDoc.data()
            if (pageData.questionBank && pageData.questionBank.length > 0) {
              pageData.questionBank.forEach((qItem, idx) => {
                const tbKey = `${tbDoc.id}_${pageDoc.id}_${qItem.text}`
                if (!answeredTextbookKeys.has(tbKey)) {
                  allQuestions.push({
                    id: `tb_${tbDoc.id}_${pageDoc.id}_${idx}`,
                    question: { text: qItem.text, latex: qItem.text },
                    topic: qItem.topic || null,
                    sourceLabel: `${tbData.title || 'Textbook'}, Page ${pageData.pageNumber}`,
                    sourceType: 'textbook',
                    timestamp: pageData.timestamp || null
                  })
                }
              })
            }
          })
        }
      } catch (tbError) {
        console.error('Error loading textbook questions for view work:', tbError)
      }
      
      setStudyQuestions(allQuestions)
    } catch (error) {
      console.error('Error loading study questions:', error)
    } finally {
      setLoadingQuestions(false)
    }
  }

  // First page of explain sessions for the merged My Work list. Check-answer
  // history above is read in full, so only this half needs pagination.
  const loadExplainSessionsFirstPage = async () => {
    if (!studentUid) return
    setLoadingExplainSessions(true)
    setExplainSessionsLoadError(null)
    try {
      const page = await fetchExplainSessions(studentUid)
      setExplainSessionsList(page.sessions)
      setExplainSessionsCursor(page.cursor)
      setExplainSessionsHasMore(page.hasMore)
    } catch (error) {
      console.error('Error loading explain sessions for My Work:', error)
      setExplainSessionsLoadError(EXPLAIN_SESSIONS_LOAD_ERROR)
    } finally {
      setLoadingExplainSessions(false)
    }
  }

  const handleLoadMoreExplainSessions = async () => {
    if (loadingMoreExplainSessions || !explainSessionsCursor || !studentUid) return
    setLoadingMoreExplainSessions(true)
    setExplainSessionsLoadError(null)
    try {
      const page = await fetchExplainSessions(studentUid, { cursor: explainSessionsCursor })
      setExplainSessionsList(previous => {
        const seen = new Set(previous.map(session => session.id))
        return [...previous, ...page.sessions.filter(session => !seen.has(session.id))]
      })
      setExplainSessionsCursor(page.cursor)
      setExplainSessionsHasMore(page.hasMore)
    } catch (error) {
      console.error('Error loading more explain sessions for My Work:', error)
      setExplainSessionsLoadError(EXPLAIN_SESSIONS_LOAD_ERROR)
    } finally {
      setLoadingMoreExplainSessions(false)
    }
  }

  // Chat is loaded on demand when an explain session is opened in the merged
  // detail pane, never alongside the list itself.
  const loadViewWorkExplainChat = useCallback((session) => {
    const requestId = viewWorkChatRequestRef.current + 1
    viewWorkChatRequestRef.current = requestId

    setViewWorkChat([])
    setViewWorkChatState(session.chatMessageCount > 0 ? 'loading' : 'loaded')
    if (session.chatMessageCount === 0 || !studentUid) return

    fetchExplainChat(studentUid, session.id)
      .then((messages) => {
        if (viewWorkChatRequestRef.current !== requestId) return
        setViewWorkChat(messages)
        setViewWorkChatState('loaded')
      })
      .catch((error) => {
        console.error('Error loading explain chat for My Work:', error)
        if (viewWorkChatRequestRef.current !== requestId) return
        setViewWorkChatState('error')
      })
  }, [studentUid])

  const loadAvailableQuestions = async () => {
    if (!studentUid) return
    try {
      setLoadingAvailableQuestions(true)
      const allQuestions = []

      const questionsRef = collection(db, 'users', studentUid, 'studyQuestions')
      const parentQ = query(questionsRef, where('source', '==', 'parent-managed'))
      const parentSnapshot = await getDocs(parentQ)
      parentSnapshot.docs.forEach(d => {
        const data = d.data()
        allQuestions.push({
          id: d.id,
          ...data,
          sourceLabel: 'From Parent',
          sourceType: 'parent-managed',
          isAnswered: data.status === 'completed'
        })
      })

      const answeredQuestionsRef = collection(db, 'users', studentUid, 'studyQuestions')
      const answeredQ = query(answeredQuestionsRef, where('sourceType', '==', 'textbook'))
      const answeredSnapshot = await getDocs(answeredQ)
      const answeredTextbookKeys = new Set()
      answeredSnapshot.docs.forEach(d => {
        const data = d.data()
        if (data.textbookId && data.pageId) {
          const qText = data.question?.text || data.question?.latex || ''
          answeredTextbookKeys.add(`${data.textbookId}_${data.pageId}_${qText}`)
        }
      })

      const answeredPageQ = query(answeredQuestionsRef, where('sourceType', '==', 'page'))
      const answeredPageSnapshot = await getDocs(answeredPageQ)
      const answeredPageKeys = new Set()
      answeredPageSnapshot.docs.forEach(d => {
        const data = d.data()
        if (data.pageId) {
          const qText = data.question?.text || data.question?.latex || ''
          answeredPageKeys.add(`${data.pageId}_${qText}`)
        }
      })

      try {
        const textbooksRef = collection(db, 'textbooks')
        const tbQ = query(textbooksRef, where('studentIds', 'array-contains', studentUid))
        const tbSnapshot = await getDocs(tbQ)

        for (const tbDoc of tbSnapshot.docs) {
          const tbData = tbDoc.data()
          const pagesRef = collection(db, 'textbooks', tbDoc.id, 'pages')
          const pagesQ = query(pagesRef, orderBy('pageNumber', 'asc'))
          const pagesSnapshot = await getDocs(pagesQ)

          pagesSnapshot.docs.forEach(pageDoc => {
            const pageData = pageDoc.data()
            if (pageData.questionBank && pageData.questionBank.length > 0) {
              pageData.questionBank.forEach((q, idx) => {
                const tbKey = `${tbDoc.id}_${pageDoc.id}_${q.text}`
                allQuestions.push({
                  id: `${tbDoc.id}_${pageDoc.id}_${idx}`,
                  question: { text: q.text, latex: q.text },
                  topic: q.topic || null,
                  mathBranch: q.mathBranch || null,
                  difficulty: q.difficulty || null,
                  questionHash: q.questionHash || null,
                  sourceLabel: `${tbData.title || 'Textbook'}, Page ${pageData.pageNumber}`,
                  sourceType: 'textbook',
                  textbookId: tbDoc.id,
                  textbookTitle: tbData.title || 'Textbook',
                  pageNumber: pageData.pageNumber,
                  pageId: pageDoc.id,
                  isAnswered: answeredTextbookKeys.has(tbKey)
                })
              })
            }
          })
        }
      } catch (tbError) {
        console.error('Error loading textbook questions:', tbError)
      }

      try {
        const userPagesRef = collection(db, 'users', studentUid, 'pages')
        const userPagesQ = query(userPagesRef, orderBy('pageNumber', 'asc'))
        const userPagesSnapshot = await getDocs(userPagesQ)

        userPagesSnapshot.docs.forEach(pageDoc => {
          const pageData = pageDoc.data()
          if (pageData.questionBank && pageData.questionBank.length > 0) {
            pageData.questionBank.forEach((q, idx) => {
              const pageKey = `${pageDoc.id}_${q.text}`
              allQuestions.push({
                id: `page_${pageDoc.id}_${idx}`,
                question: { text: q.text, latex: q.text },
                topic: q.topic || null,
                mathBranch: q.mathBranch || null,
                difficulty: q.difficulty || null,
                questionHash: q.questionHash || null,
                sourceLabel: `Page ${pageData.pageNumber}`,
                sourceType: 'page',
                pageId: pageDoc.id,
                pageNumber: pageData.pageNumber,
                isAnswered: answeredPageKeys.has(pageKey)
              })
            })
          }
        })
      } catch (pageError) {
        console.error('Error loading user pages:', pageError)
      }

      setAvailableQuestions(allQuestions)
    } catch (error) {
      console.error('Error loading available questions:', error)
    } finally {
      setLoadingAvailableQuestions(false)
    }
  }

  const autoSaveQuestion = async (questionData, metadata, attempts, finalResult) => {
    if (!studentUid || !questionData) return
    try {
      const saveData = {
        question: {
          text: questionData.text || '',
          latex: questionData.latex || '',
          confidence: questionData.confidence || 0
        },
        mathBranch: metadata?.mathBranch || null,
        topic: metadata?.topic || null,
        difficulty: metadata?.difficulty || null,
        attempts: attempts || [],
        status: finalResult ? 'completed' : 'in-progress',
        finalResult: finalResult || null,
        timestamp: serverTimestamp()
      }
      if (autoSaveDocIdRef.current) {
        await updateDoc(doc(db, 'users', studentUid, 'studyQuestions', autoSaveDocIdRef.current), saveData)
      } else {
        const studyQuestionsRef = collection(db, 'users', studentUid, 'studyQuestions')
        const newDoc = await addDoc(studyQuestionsRef, saveData)
        autoSaveDocIdRef.current = newDoc.id
      }
    } catch (error) {
      console.error('Error auto-saving question:', error)
    }
  }

  const handleSelectAvailableQuestion = (q) => {
    const questionData = { text: q.question?.text || '', latex: q.question?.latex || q.question?.text || '' }
    const metadata = {
      questionHash: q.questionHash || null,
      mathBranch: q.mathBranch || null,
      topic: q.topic || null,
      difficulty: q.difficulty || null
    }
    setQuestion(questionData)
    setQuestionMetadata(metadata)
    setAnswer(null)
    setFeedback(null)
    setAttemptNumber(1)
    setIsCelebrating(false)
    setAllAttempts([])
    setChatConversation(null)
    setPendingStudyQuestion(null)
    setSelectedAssignment({
      id: q.id,
      sourceType: q.sourceType,
      sourceLabel: q.sourceLabel,
      textbookId: q.textbookId || null,
      pageId: q.pageId || null,
      pageNumber: q.pageNumber || null,
      textbookTitle: q.textbookTitle || null
    })
    setHelperIntent('check-answer')
    setSelectedMenuItem('helper')

    if (q.sourceType === 'parent-managed') {
      autoSaveDocIdRef.current = q.id
    } else {
      autoSaveDocIdRef.current = null
      autoSaveQuestion(questionData, metadata, [], null)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
      history.push('/')
    } catch (error) {
      console.error('Error logging out:', error)
    }
  }

  const handleNewQuestionClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    try {
      if (!file.type.startsWith('image/')) {
        return
      }

      const imageUrl = URL.createObjectURL(file)
      setImageToCrop(imageUrl)
      setShowImageCrop(true)

    } catch (error) {
      console.error('Error handling photo upload:', error)
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleTakePhoto = () => {
    // Phones: the native camera app via the file input. Laptops/PCs: the
    // in-page webcam, never the file explorer — `capture` on a file input
    // is ignored on desktop and Chrome falls back to "Open".
    if (isMobile) {
      if (fileInputRef.current) {
        fileInputRef.current.click()
      }
      return
    }
    handleOpenCamera()
  }

  const stopCameraTracks = () => {
    const stream = cameraStreamRef.current
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      cameraStreamRef.current = null
    }
    setCameraStream(null)
  }

  const requestCameraStream = async () => {
    if (!navigator.mediaDevices || typeof navigator.mediaDevices.getUserMedia !== 'function') {
      const error = new Error('Camera API is not available in this browser.')
      error.name = 'NotSupportedError'
      throw error
    }

    // Laptops expose a user-facing webcam. `facingMode: environment` is a
    // phone rear-camera constraint and often fails (or is ignored) on PCs.
    const attempts = [
      { video: { facingMode: 'user', width: { ideal: 1920 }, height: { ideal: 1080 } } },
      { video: { facingMode: { ideal: 'user' } } },
      { video: true }
    ]

    let lastError = null
    for (const constraints of attempts) {
      try {
        return await navigator.mediaDevices.getUserMedia(constraints)
      } catch (error) {
        lastError = error
      }
    }
    throw lastError || new Error('Could not access camera.')
  }

  const handleOpenCamera = async () => {
    stopCameraTracks()
    setCameraError(null)
    setShowCameraModal(true)
    cameraAbortRef.current = false

    let stream = null
    try {
      stream = await requestCameraStream()

      if (cameraAbortRef.current) {
        stream.getTracks().forEach((track) => track.stop())
        return
      }

      cameraStreamRef.current = stream
      setCameraStream(stream)
    } catch (error) {
      if (cameraAbortRef.current) {
        if (stream) {
          stream.getTracks().forEach((track) => track.stop())
        }
        return
      }

      console.error('Error accessing camera:', error)
      if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
        setCameraError('Camera access was denied. Please allow camera access in your browser settings.')
      } else if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
        setCameraError('No camera found on this device.')
      } else if (error?.name === 'NotSupportedError' || error?.name === 'NotReadableError') {
        setCameraError('This browser could not open the camera. Close other apps using it and try again, or use Choose File.')
      } else if (window.isSecureContext === false) {
        setCameraError('The camera can only be used on a secure (https) connection.')
      } else {
        setCameraError('Could not access camera. Please try again, or use Choose File.')
      }
    }
  }

  const handleCloseCamera = () => {
    cameraAbortRef.current = true
    stopCameraTracks()
    setShowCameraModal(false)
    setCameraError(null)
  }

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    if (!video.videoWidth || !video.videoHeight) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight

    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0)

    canvas.toBlob((blob) => {
      if (blob) {
        const imageUrl = URL.createObjectURL(blob)
        setImageToCrop(imageUrl)
        setShowImageCrop(true)
        handleCloseCamera()
      }
    }, 'image/jpeg', 0.95)
  }

  const beginCheckAnswerFromQuestion = async (result, helperAttemptId) => {
    autoSaveDocIdRef.current = null
    if (helperAttemptId) helperAttemptIdRef.current = helperAttemptId

    setAnalyzingQuestion(true)
    let savedMetadata = null
    try {
      const analyzeStudyQuestion = httpsCallable(functions, 'analyzeStudyQuestion')
      const analysisResult = await analyzeStudyQuestion({
        ...actingStudentPayload,
        questionLatex: result.latex,
        questionText: result.text,
        helperAttemptId: helperAttemptId || helperAttemptIdRef.current
      })
      applyHelperQuota(analysisResult.data)

      if (analysisResult.data && analysisResult.data.success) {
        savedMetadata = {
          questionHash: analysisResult.data.questionHash,
          mathBranch: analysisResult.data.mathBranch,
          topic: analysisResult.data.topic,
          difficulty: analysisResult.data.difficulty
        }
        setQuestionMetadata(savedMetadata)
      }
    } catch (analysisError) {
      if (openHelperQuotaModal(analysisError)) {
        setAnalyzingQuestion(false)
        throw analysisError
      }
      console.error('Error analyzing question with Claude:', analysisError)
    } finally {
      setAnalyzingQuestion(false)
    }

    setQuestion(result)
    await autoSaveQuestion(result, savedMetadata, [], null)
    setIsTypingProblem(false)
    setTypedProblem('')
    setHelperEntryError(null)
  }

  const processImageWithMathpix = async (imageBase64, size, filename) => {
    const attemptId = mintHelperAttemptId()
    helperAttemptIdRef.current = attemptId
    try {
      const processMathpixOCR = httpsCallable(functions, 'processMathpixOCR')
      const mathpixResult = await processMathpixOCR({
        ...actingStudentPayload,
        imageBase64: imageBase64,
        filename: filename,
        helperAttemptId: attemptId
      })

      if (!mathpixResult.data || !mathpixResult.data.success) {
        throw new Error('Failed to process image with Mathpix')
      }

      const result = {
        text: mathpixResult.data.text || '',
        latex: mathpixResult.data.latex || mathpixResult.data.text || '',
        confidence: mathpixResult.data.confidence || 0,
        mathml: mathpixResult.data.mathml || ''
      }

      await beginCheckAnswerFromQuestion(result, attemptId)
    } catch (error) {
      console.error('Error processing image:', error)
      if (openHelperQuotaModal(error)) return
      setHelperEntryError(explainErrorMessage(
        error,
        "We couldn't read that image. Try a clearer photo of the problem."
      ))
    } finally {
      setProcessingImage(false)
    }
  }

  const handleSubmitTypedProblem = async () => {
    if (processingImage || typedSubmitLockRef.current) return

    const typed = typedProblem.trim()
    if (!typed) {
      setHelperEntryError(TYPED_PROBLEM_EMPTY_MESSAGE)
      return
    }
    if (typed.length > TYPED_PROBLEM_MAX_CHARS) {
      setHelperEntryError(
        `That problem is too long (maximum ${TYPED_PROBLEM_MAX_CHARS} characters)`
      )
      return
    }

    typedSubmitLockRef.current = true
    setHelperEntryError(null)
    setProcessingImage(true)
    setExplainStage('converting')
    const attemptId = mintHelperAttemptId()
    helperAttemptIdRef.current = attemptId
    try {
      const convertTypedMathProblem = httpsCallable(functions, 'convertTypedMathProblem')
      const converted = await convertTypedMathProblem({
        ...actingStudentPayload,
        typedProblem: typed,
        helperAttemptId: attemptId
      })
      const latex = converted.data && typeof converted.data.latex === 'string'
        ? converted.data.latex.trim()
        : ''
      if (!converted.data || !converted.data.success || !latex) {
        throw new Error('convertTypedMathProblem returned no latex')
      }

      if (helperIntent === 'explain') {
        explainProblemImageRef.current = ''
        await startExplainFromQuestion({
          questionLatex: latex,
          questionText: typed,
          questionConfidence: 1,
          helperAttemptId: attemptId
        })
      } else {
        await beginCheckAnswerFromQuestion({
          text: typed,
          latex,
          confidence: 1,
          mathml: ''
        }, attemptId)
      }
    } catch (error) {
      console.error('Error converting typed math problem:', error)
      if (openHelperQuotaModal(error)) return
      setHelperEntryError(explainErrorMessage(error, TYPED_PROBLEM_CONVERT_FALLBACK))
    } finally {
      typedSubmitLockRef.current = false
      setProcessingImage(false)
      setExplainStage(null)
    }
  }

  const processAnswerWithClaude = async (imageBlob) => {
    try {
      setCheckingAnswer(true)

      const imageBase64 = await readFileAsBase64(imageBlob)

      let latex, text, isCorrect, aiFeedback

      if (questionMetadata?.mathBranch === 'Algebra') {
        const processMathpixOCR = httpsCallable(functions, 'processMathpixOCR')
        const mathpixResult = await processMathpixOCR({
          ...actingStudentPayload,
          imageBase64: imageBase64,
          helperAttemptId: helperAttemptIdRef.current,
          questionHash: questionMetadata?.questionHash || ''
        })

        if (!mathpixResult.data || !mathpixResult.data.success) {
          throw new Error('Failed to process answer image with Mathpix')
        }

        latex = mathpixResult.data.latex || ''
        text = mathpixResult.data.text || ''

        const gradeStudyAnswerFromLatex = httpsCallable(functions, 'gradeStudyAnswerFromLatex')
        const gradeResult = await gradeStudyAnswerFromLatex({
          ...actingStudentPayload,
          answerLatex: latex,
          answerText: text,
          questionHash: questionMetadata?.questionHash,
          questionLatex: question.latex,
          questionText: question.text,
          helperAttemptId: helperAttemptIdRef.current
        })
        applyHelperQuota(gradeResult.data)

        if (!gradeResult.data || !gradeResult.data.success) {
          throw new Error('Failed to grade answer')
        }

        isCorrect = gradeResult.data.isCorrect
        aiFeedback = gradeResult.data.feedback
      } else {
        const gradeStudyAnswer = httpsCallable(functions, 'gradeStudyAnswer')
        const result = await gradeStudyAnswer({
          ...actingStudentPayload,
          helperAttemptId: helperAttemptIdRef.current,
          imageBase64: imageBase64,
          questionHash: questionMetadata?.questionHash,
          questionLatex: question.latex,
          questionText: question.text
        })
        applyHelperQuota(result.data)

        if (!result.data || !result.data.success) {
          throw new Error('Failed to process answer image')
        }

        latex = result.data.latex
        text = result.data.text
        isCorrect = result.data.isCorrect
        aiFeedback = result.data.feedback
      }

      const answerResult = {
        text: text || '',
        latex: latex || '',
        confidence: 1
      }
      setAnswer(answerResult)

      const currentAttempt = {
        attemptNumber: attemptNumber,
        answer: {
          text: answerResult.text,
          latex: answerResult.latex,
          confidence: answerResult.confidence
        },
        feedback: {
          isCorrect: isCorrect,
          message: isCorrect ? `CORRECT: ${aiFeedback}` : `INCORRECT: ${aiFeedback}`
        }
      }

      const updatedAttempts = [...allAttempts, currentAttempt]
      setAllAttempts(updatedAttempts)

      setFeedback({
        isCorrect: isCorrect,
        message: isCorrect ? `CORRECT: ${aiFeedback}` : `INCORRECT: ${aiFeedback}`
      })

      triggerCelebration(isCorrect, attemptNumber)

      const finalResult = (isCorrect || attemptNumber >= 3)
        ? (isCorrect ? 'correct' : 'incorrect_after_3_attempts')
        : null

      if (finalResult) {
        setPendingStudyQuestion({
          question: {
            text: question.text || '',
            latex: question.latex || '',
            confidence: question.confidence || 0
          },
          mathBranch: questionMetadata?.mathBranch || null,
          topic: questionMetadata?.topic || null,
          difficulty: questionMetadata?.difficulty || null,
          attempts: updatedAttempts,
          finalResult: finalResult
        })
      }

      await autoSaveQuestion(question, questionMetadata, updatedAttempts, finalResult)

    } catch (error) {
      console.error('Error processing answer with Claude:', error)
      openHelperQuotaModal(error)
    } finally {
      setProcessingImage(false)
      setCheckingAnswer(false)
    }
  }

  const handleCropComplete = async (croppedBlob) => {
    try {
      setShowImageCrop(false)
      const originalImageUrl = imageToCrop
      setImageToCrop(null)
      setProcessingImage(true)

      let imageToProcess

      if (croppedBlob === null) {
        const response = await fetch(originalImageUrl)
        const originalBlob = await response.blob()
        imageToProcess = await compressImage(originalBlob, 1.5)
      } else {
        imageToProcess = await compressImage(croppedBlob, 1.5)
      }

      // An explain-intent upload never reaches the check-answer analyze/grade
      // pipeline, and an upload arriving while a lesson is open is discarded
      // rather than silently replacing it.
      if (helperIntent === 'explain') {
        if (!explainSession) {
          await startExplainSession(imageToProcess)
        }
        return
      }

      if (question) {
        await processAnswerWithClaude(imageToProcess)
        return
      }

      const imageBase64 = await readFileAsBase64(imageToProcess)

      await processImageWithMathpix(imageBase64, imageToProcess.size, 'question-image.jpg')

    } catch (error) {
      console.error('Error processing cropped image:', error)
      setHelperEntryError("We couldn't read that image. Try again with a clearer photo of the problem.")
    } finally {
      setProcessingImage(false)
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleCropCancel = () => {
    setShowImageCrop(false)
    setImageToCrop(null)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClearAnswer = () => {
    const sessionCompleted = pendingStudyQuestion != null
    setQuestion(null)
    setQuestionMetadata(null)
    setAnswer(null)
    setFeedback(null)
    setAttemptNumber(1)
    setAllAttempts([])
    setChatConversation(null)
    setPendingStudyQuestion(null)
    setSelectedAssignment(null)
    setIsCelebrating(false)
    autoSaveDocIdRef.current = null
    setHelperIntent(null)
    setIsTypingProblem(false)
    setTypedProblem('')
    setHelperEntryError(null)
    if (sessionCompleted) openDeferredQuotaModal()
  }

  const handleTryAgain = () => {
    setAnswer(null)
    setFeedback(null)
    setAttemptNumber(attemptNumber + 1)
  }

  const handleSave = async () => {
    if (pendingStudyQuestion && studentUid) {
      try {
        const studyQuestionData = {
          ...pendingStudyQuestion,
          timestamp: serverTimestamp()
        }
        if (selectedAssignment) {
          studyQuestionData.sourceType = selectedAssignment.sourceType
          studyQuestionData.sourceLabel = selectedAssignment.sourceLabel
          if (selectedAssignment.textbookId) {
            studyQuestionData.textbookId = selectedAssignment.textbookId
            studyQuestionData.pageId = selectedAssignment.pageId
            studyQuestionData.pageNumber = selectedAssignment.pageNumber
            studyQuestionData.textbookTitle = selectedAssignment.textbookTitle
          } else if (selectedAssignment.sourceType === 'page' && selectedAssignment.pageId) {
            studyQuestionData.pageId = selectedAssignment.pageId
            studyQuestionData.pageNumber = selectedAssignment.pageNumber
          }
        }
        if (chatConversation && chatConversation.length > 0) {
          studyQuestionData.chatConversation = chatConversation.map(msg => ({
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp
          }))
        }

        let questionDoc
        if (selectedAssignment && selectedAssignment.sourceType === 'parent-managed') {
          const existingRef = doc(db, 'users', studentUid, 'studyQuestions', selectedAssignment.id)
          await updateDoc(existingRef, {
            ...studyQuestionData,
            status: 'completed'
          })
          questionDoc = { id: selectedAssignment.id }
        } else if (autoSaveDocIdRef.current) {
          await updateDoc(doc(db, 'users', studentUid, 'studyQuestions', autoSaveDocIdRef.current), {
            ...studyQuestionData,
            status: 'completed'
          })
          questionDoc = { id: autoSaveDocIdRef.current }
        } else {
          const studyQuestionsRef = collection(db, 'users', studentUid, 'studyQuestions')
          questionDoc = await addDoc(studyQuestionsRef, {
            ...studyQuestionData,
            status: 'completed'
          })
        }

        const quotaSource = helperQuotaSource()
        Promise.all([
          getVideoRecommendations(
            pendingStudyQuestion.question.latex || pendingStudyQuestion.question.text,
            pendingStudyQuestion.attempts,
            quotaSource
          ).catch(error => {
            console.error('Error getting video recommendations:', error)
            return []
          }),
          generateParentSummaries(
            pendingStudyQuestion.question.latex || pendingStudyQuestion.question.text,
            pendingStudyQuestion.attempts,
            quotaSource
          ).catch(error => {
            console.error('Error generating parent summaries:', error)
            return null
          })
        ]).then(async ([recommendations, summaries]) => {
          try {
            if (summaries && summaries.answerSummary) {
              await updateDoc(doc(db, 'users', studentUid, 'studyQuestions', questionDoc.id), {
                parentFeedback: summaries
              })
            }

            if (recommendations && Array.isArray(recommendations) && recommendations.length > 0) {
              const videoRecsRef = collection(db, 'users', studentUid, 'studyQuestions', questionDoc.id, 'videoRecommendations')

              for (const video of recommendations) {
                await addDoc(videoRecsRef, {
                  title: video.title,
                  url: video.url,
                  thumbnail: video.thumbnail || '',
                  timestamp: serverTimestamp()
                })
              }
            }
          } catch (error) {
            console.error('Error saving parent feedback:', error)
          }
        }).catch(error => {
          console.error('Error processing parent feedback:', error)
        })
      } catch (saveError) {
        console.error('Error saving study question:', saveError)
      }
    }

    setQuestion(null)
    setQuestionMetadata(null)
    setAnswer(null)
    setFeedback(null)
    setAttemptNumber(1)
    setAllAttempts([])
    setChatConversation(null)
    setPendingStudyQuestion(null)
    setSelectedAssignment(null)
    setIsCelebrating(false)
    autoSaveDocIdRef.current = null
    setHelperIntent(null)
    setIsTypingProblem(false)
    setTypedProblem('')
    setHelperEntryError(null)
    openDeferredQuotaModal()

    await loadStudyQuestions()
    await loadAvailableQuestions()
  }

  const handleChatMessagesUpdate = (messages) => {
    setChatConversation(messages)
  }

  const handleViewWorkSortClick = () => {
    setShowViewWorkSortDropdown(!showViewWorkSortDropdown)
  }

  const handleViewWorkSortSelect = (sortType) => {
    setSortBy(sortType)
    setShowViewWorkSortDropdown(false)
  }

  const handleToggleViewWorkSortOrder = () => {
    setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')
  }

  const handleSelectViewWorkQuestion = (item) => {
    setSelectedViewWorkQuestion(item)
    if (item.workItemType === 'explain') {
      loadViewWorkExplainChat(item)
    }
  }

  const getQuestionText = (q) => {
    return q.question?.latex || 
           q.question?.text || 
           q.questionText ||
           (typeof q.question === 'string' ? q.question : 'No question text')
  }

  const getQuestionAttempts = (q) => {
    return q.attempts && q.attempts.length > 0 
      ? q.attempts 
      : q.allAttempts && q.allAttempts.length > 0
      ? q.allAttempts
      : [{
          answer: q.answer,
          feedback: q.feedback,
          studentAnswer: q.studentAnswer
        }]
  }

  const isQuestionCorrect = (q) => {
    const attempts = getQuestionAttempts(q)
    const lastAttempt = attempts[attempts.length - 1]
    return lastAttempt?.feedback?.isCorrect === true
  }

  // Common date/topic accessors for the merged My Work list, where each item
  // is either a check-answer question or an explain session.
  const getWorkItemMillis = (item) => {
    if (item.workItemType === 'explain') {
      return explainTimestampMillis(item.createdAt)
    }
    return item.timestamp?.toDate ? item.timestamp.toDate().getTime() : 0
  }

  const getWorkItemTopic = (item) => item.topic || 'No Topic'

  const renderDesktopQuestionCards = () => {
    const stillLoadingFirstPage = (loadingQuestions || loadingExplainSessions) &&
      studyQuestions.length === 0 && explainSessionsList.length === 0
    if (stillLoadingFirstPage) {
      return <div className="study-helper-desktop-loading">Loading your work...</div>
    }

    // Check-answer history and explain sessions are read from different
    // collections with different pagination models, but they share one
    // sorted, grouped list here so a student sees everything they have done
    // in one place instead of switching tabs.
    const workItems = [
      ...studyQuestions.map(q => ({ ...q, workItemType: 'check-answer' })),
      ...explainSessionsList.map(session => ({ ...session, workItemType: 'explain' }))
    ]

    if (workItems.length === 0) {
      return (
        <div className="study-helper-desktop-empty-questions">
          <p>No work found yet.</p>
          {explainSessionsLoadError ? (
            <div className="parent-explain-load-error">
              <p>{explainSessionsLoadError}</p>
              <button
                type="button"
                className="parent-explain-retry-btn"
                onClick={loadExplainSessionsFirstPage}
              >
                Try again
              </button>
            </div>
          ) : (
            <p style={{ fontSize: 'calc(14px * var(--font-scale))', opacity: 0.7 }}>Start by creating a new question!</p>
          )}
        </div>
      )
    }

    const groupedItems = workItems.reduce((groups, item) => {
      let groupKey
      if (sortBy === 'date') {
        const millis = getWorkItemMillis(item)
        groupKey = millis
          ? new Date(millis).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            })
          : 'Unknown date'
      } else if (sortBy === 'topic') {
        groupKey = getWorkItemTopic(item)
      } else if (sortBy === 'correctness') {
        if (item.workItemType === 'explain') {
          groupKey = 'Explain sessions'
        } else {
          const isCorrect = isQuestionCorrect(item)
          groupKey = isCorrect ? 'Correct' : 'Incorrect'
        }
      }

      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(item)
      return groups
    }, {})

    // Newest first inside every group regardless of sort mode; sortOrder only
    // controls how the groups themselves are ordered.
    Object.keys(groupedItems).forEach(key => {
      groupedItems[key].sort((a, b) => getWorkItemMillis(b) - getWorkItemMillis(a))
    })

    let sortedGroupKeys = Object.keys(groupedItems)
    if (sortBy === 'date') {
      sortedGroupKeys.sort((a, b) => {
        if (a === 'Unknown date') return 1
        if (b === 'Unknown date') return -1
        const dateA = new Date(a)
        const dateB = new Date(b)
        return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
      })
    } else if (sortBy === 'topic') {
      sortedGroupKeys.sort((a, b) => {
        return sortOrder === 'desc' ? b.localeCompare(a) : a.localeCompare(b)
      })
    } else if (sortBy === 'correctness') {
      const rank = sortOrder === 'desc'
        ? { Correct: 0, Incorrect: 1, 'Explain sessions': 2 }
        : { Incorrect: 0, Correct: 1, 'Explain sessions': 2 }
      sortedGroupKeys.sort((a, b) => rank[a] - rank[b])
    }

    return (
      <div className={`study-helper-desktop-view-work-layout ${isMobile && selectedViewWorkQuestion ? 'question-selected' : ''}`}>
        <div className="study-helper-desktop-questions-menu">
          <div className="study-helper-desktop-questions-menu-header">
            <div className="study-helper-desktop-questions-title">
              My Work
            </div>
            <div className="study-helper-desktop-questions-menu-controls">
              <div className="study-helper-desktop-sort-container">
                <button className="study-helper-desktop-sort-button" onClick={handleViewWorkSortClick} title="Sort options">
                  <SortIcon />
                </button>
                <button 
                  className="study-helper-desktop-sort-order-button" 
                  onClick={handleToggleViewWorkSortOrder}
                  title={sortOrder === 'asc' ? 'Toggle descending order' : 'Toggle ascending order'}
                >
                  {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                </button>
                {showViewWorkSortDropdown && (
                  <div className="study-helper-desktop-sort-dropdown">
                    <div className="dropdown-item" onClick={() => handleViewWorkSortSelect('date')}>Date</div>
                    <div className="dropdown-item" onClick={() => handleViewWorkSortSelect('topic')}>Topic</div>
                    <div className="dropdown-item" onClick={() => handleViewWorkSortSelect('correctness')}>Correct/Incorrect</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="study-helper-desktop-questions-list-scroll">
            {sortedGroupKeys.map(groupName => (
              <div key={groupName} className="study-helper-desktop-date-section">
                <div className="study-helper-desktop-date-header">{groupName}</div>
                {groupedItems[groupName].map(item => {
                  const isSelected = selectedViewWorkQuestion?.id === item.id &&
                    selectedViewWorkQuestion?.workItemType === item.workItemType

                  if (item.workItemType === 'explain') {
                    return (
                      <div
                        key={`explain_${item.id}`}
                        className={`study-helper-desktop-question-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleSelectViewWorkQuestion(item)}
                      >
                        <div className="parent-explain-item-tags">
                          <span className="student-explain-type-chip">Explain</span>
                          <span className={`parent-explain-status-chip ${item.status}`}>
                            {explainStatusLabel(item.status)}
                          </span>
                        </div>
                        <div className="study-helper-desktop-question-preview">
                          <MixedContentPreview content={explainSessionQuestionText(item)} fontSize="14px" />
                        </div>
                        <div className="study-helper-desktop-question-source-label">
                          {formatExplainDate(item.createdAt) || 'Date unavailable'}
                          {item.topic ? ` · ${item.topic}` : ''}
                        </div>
                      </div>
                    )
                  }

                  const questionText = getQuestionText(item)
                  const attempts = getQuestionAttempts(item)
                  const hasGradedAttempt = attempts.some(
                    attempt => attempt?.feedback && typeof attempt.feedback.isCorrect === 'boolean'
                  )
                  const isCorrect = hasGradedAttempt && isQuestionCorrect(item)

                  return (
                    <div 
                      key={`check-answer_${item.id}`} 
                      className={`study-helper-desktop-question-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleSelectViewWorkQuestion(item)}
                    >
                      <div className="parent-explain-item-tags">
                        <span className="student-explain-type-chip check-answer">Check answer</span>
                        {hasGradedAttempt && (
                          <span className={`parent-explain-status-chip ${isCorrect ? 'completed' : 'abandoned'}`}>
                            {isCorrect ? 'Correct' : 'Needs work'}
                          </span>
                        )}
                      </div>
                      <div className="study-helper-desktop-question-preview">
                        <MixedContentPreview content={questionText} fontSize="14px" />
                      </div>
                      {item.sourceLabel && (
                        <div className="study-helper-desktop-question-source-label">{item.sourceLabel}</div>
                      )}
                    </div>
                  )
                })}
              </div>
            ))}

            {explainSessionsHasMore && (
              <button
                type="button"
                className="parent-explain-load-more-btn"
                onClick={handleLoadMoreExplainSessions}
                disabled={loadingMoreExplainSessions}
              >
                {loadingMoreExplainSessions ? 'Loading...' : `Load ${EXPLAIN_SESSION_PAGE_SIZE} more explain sessions`}
              </button>
            )}

            {explainSessionsLoadError && explainSessionsList.length > 0 && (
              <div className="parent-explain-inline-error" role="alert">{explainSessionsLoadError}</div>
            )}
          </div>
        </div>
        <div className="study-helper-desktop-answers-viewer">
          {selectedViewWorkQuestion ? (
            selectedViewWorkQuestion.workItemType === 'explain' ? (
              <div className="student-explain-detail" data-report-exercise="true">
                <button
                  type="button"
                  className="study-helper-desktop-mobile-back-to-questions"
                  onClick={() => setSelectedViewWorkQuestion(null)}
                >
                  ← Back to My Work
                </button>

                {isExplainSessionResumable(selectedViewWorkQuestion) && (
                  <div className="student-explain-resume-bar">
                    <p className="student-explain-resume-copy">
                      You can pick this session back up where you left off.
                    </p>
                    <button
                      type="button"
                      className="student-explain-resume-btn"
                      onClick={() => resumeExplainFromViewWork(selectedViewWorkQuestion)}
                    >
                      Continue in Helper
                    </button>
                  </div>
                )}

                <ExplainSessionReview
                  session={selectedViewWorkQuestion}
                  showChat={true}
                  chat={viewWorkChat}
                  chatState={viewWorkChatState}
                  chatError={viewWorkChatState === 'error' ? EXPLAIN_CHAT_LOAD_ERROR : null}
                  onRetryChat={() => loadViewWorkExplainChat(selectedViewWorkQuestion)}
                  headerActions={null}
                />
              </div>
            ) : (
            <div className="study-helper-desktop-question-detail" data-report-exercise="true">
              <button
                className="study-helper-desktop-mobile-back-to-questions"
                onClick={() => setSelectedViewWorkQuestion(null)}
              >
                ← Back to Questions
              </button>
              <div className="study-helper-desktop-detail-column">
                <div className="study-helper-desktop-question-text-section">
                  <div className="study-helper-desktop-detail-label">Question</div>
                  {selectedViewWorkQuestion.sourceLabel && (
                    <div className="study-helper-desktop-question-source-label">{selectedViewWorkQuestion.sourceLabel}</div>
                  )}
                  {selectedViewWorkQuestion.timestamp && (
                    <div className="study-helper-desktop-question-timestamp">
                      {selectedViewWorkQuestion.timestamp.toDate?.().toLocaleString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </div>
                  )}
                  <div className="study-helper-desktop-detail-content">
                    <MixedContentPreview 
                      content={getQuestionText(selectedViewWorkQuestion)} 
                      fontSize="18px"
                    />
                  </div>
                </div>

                {(() => {
                  const attempts = getQuestionAttempts(selectedViewWorkQuestion)

                  return (
                    <div className="study-helper-desktop-attempts-section">
                      <div className="study-helper-desktop-detail-label">
                        {attempts.length > 1 ? `All Attempts (${attempts.length})` : 'Attempt'}
                      </div>
                      <div className="study-helper-desktop-attempts-list">
                        {attempts.map((attempt, index) => {
                          const attemptAnswer = attempt.answer?.latex || 
                                               attempt.answer?.text || 
                                               attempt.studentAnswer ||
                                               'No answer recorded'
                          const attemptFeedback = attempt.feedback?.message || 'No feedback'
                          const attemptIsCorrect = attempt.feedback?.isCorrect === true

                          return (
                            <div key={index} className="study-helper-desktop-attempt-card">
                              <div className="study-helper-desktop-attempt-layout">
                                <div className={`study-helper-desktop-attempt-status-icon ${attemptIsCorrect ? 'correct' : 'incorrect'}`}>
                                  {attemptIsCorrect ? <CheckCircleIcon /> : <BlockIcon />}
                                </div>
                                <div className="study-helper-desktop-attempt-main-content">
                                  <div className="study-helper-desktop-attempt-header">
                                    <span className="attempt-number">Attempt {index + 1}</span>
                                  </div>
                                  <div className="study-helper-desktop-attempt-content-vertical">
                                    <div className="study-helper-desktop-attempt-answer">
                                      <div className="attempt-answer-label">Answer</div>
                                      <div className="attempt-answer-content">
                                        <MixedContentPreview content={attemptAnswer} fontSize="16px" />
                                      </div>
                                    </div>
                                    <div className="study-helper-desktop-attempt-feedback">
                                      <div className="attempt-feedback-label">Feedback</div>
                                      <div className="attempt-feedback-content">
                                        {attemptFeedback}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )
                })()}
              </div>
            </div>
            )
          ) : (
            <div className="study-helper-desktop-placeholder-text">
              Select an item to view details
            </div>
          )}
        </div>
      </div>
    )
  }

  const explainFinishModalOpen = Boolean(explainCompletion) && !explainFinishModalHidden
  const isExplainPlayerActive = Boolean(
    selectedMenuItem === 'helper' && explainSession && !explainRefreshing && !explainFinishModalOpen
  )

  const renderExplainPlayerDock = () => {
    if (!isExplainPlayerActive) return null
    return (
      <div className="explain-controls-dock">
        <ExplainPlayerControls
          currentStepIndex={explainStepIndex}
          totalSteps={explainSession.steps.length}
          explanation={explainExplanationText}
          askEnabled={aiChatHelperEnabled}
          finishing={explainFinishing}
          completed={Boolean(explainCompletion)}
          speechSupported={speechSupported}
          ttsEnabled={explainTtsEnabled}
          ttsVoices={explainTtsVoices}
          ttsVoiceUri={explainTtsVoiceUri}
          onPrevious={handleExplainPrevious}
          onNext={handleExplainNext}
          onFinish={handleExplainFinish}
          onAsk={() => setShowExplainAskModal(true)}
          onToggleTts={handleToggleExplainTts}
          onChangeTtsVoice={handleExplainVoiceChange}
          solutionView={explainSolutionView}
          onChangeSolutionView={handleExplainSolutionViewChange}
        />
      </div>
    )
  }

  const helperSessionsLeft = helperQuota?.limited === true && !studentView
    ? Math.max(0, 5 - (Number(helperQuota.used) || 0))
    : null
  const helperQuotaStatus = helperSessionsLeft === null ? null : (
    <p className="helper-quota-status" role="status">
      {helperSessionsLeft} of 5 sessions left this week
    </p>
  )

  const renderHelperTab = () => {
    if (explainRefreshing) {
      return (
        <div className="study-helper-desktop-new-question-layout">
          <div className="study-helper-desktop-new-question-container">
            <ExplainAnalyzingScreen processingLabel="Breaking it into steps..." />
          </div>
        </div>
      )
    }

    if (explainSession) {
      return (
        <div className="study-helper-desktop-new-question-layout explain-live-session">
          <div className="study-helper-desktop-new-question-container">
            {helperQuotaStatus}
            <ExplainSessionView
              reportAnchor
              question={explainSession.question}
              topic={explainSession.topic}
              steps={explainSession.steps}
              finalAnswer={explainSession.finalAnswer}
              finalVisual={explainSession.finalVisual}
              currentStepIndex={explainStepIndex}
              explanationText={explainExplanationText}
              askEnabled={aiChatHelperEnabled}
              speechSupported={speechSupported}
              ttsEnabled={explainTtsEnabled}
              ttsVoices={explainTtsVoices}
              ttsVoiceUri={explainTtsVoiceUri}
              finishing={explainFinishing}
              completed={Boolean(explainCompletion)}
              errorMessage={explainError}
              hideControls
              liveSession
              groupedSolution={explainSolutionView === EXPLAIN_SOLUTION_VIEW_WORKSHEET}
              solutionView={explainSolutionView}
              onChangeSolutionView={handleExplainSolutionViewChange}
              onPrevious={handleExplainPrevious}
              onNext={handleExplainNext}
              onFinish={handleExplainFinish}
              onAsk={() => setShowExplainAskModal(true)}
              onToggleTts={handleToggleExplainTts}
              onChangeTtsVoice={handleExplainVoiceChange}
              onStartOver={handleExplainStartOver}
              onRefresh={handleExplainRefresh}
            />
          </div>
        </div>
      )
    }

    if (!question && helperIntent === null) {
      return (
        <div className="study-helper-desktop-new-question-layout">
          <div className="study-helper-desktop-new-question-container">
            {helperQuotaStatus}
            <HelperChoiceScreen
              onSelectExplain={() => {
                setHelperEntryError(null)
                setIsTypingProblem(false)
                setTypedProblem('')
                setHelperIntent('explain')
              }}
              onSelectCheckAnswer={() => {
                setHelperEntryError(null)
                setIsTypingProblem(false)
                setTypedProblem('')
                setHelperIntent('check-answer')
              }}
            />
          </div>
        </div>
      )
    }

    if (!question && (helperIntent === 'explain' || helperIntent === 'check-answer')) {
      const processingLabel = explainStage === 'planning'
        ? 'Breaking it into steps...'
        : explainStage === 'converting'
          ? 'Reading your problem...'
          : 'Reading your work...'

      return (
        <div className="study-helper-desktop-new-question-layout">
          <div className="study-helper-desktop-new-question-container">
            {helperQuotaStatus}
            {processingImage ? (
              <ExplainAnalyzingScreen processingLabel={processingLabel} />
            ) : (
              <HelperProblemEntryZone
                intent={helperIntent}
                isTyping={isTypingProblem}
                typedProblem={typedProblem}
                typedProblemRef={typedProblemRef}
                errorMessage={helperEntryError}
                disabled={processingImage}
                onChooseFile={() => {
                  setHelperEntryError(null)
                  handleNewQuestionClick()
                }}
                onTakePhoto={() => {
                  setHelperEntryError(null)
                  handleTakePhoto()
                }}
                onTypeProblem={() => {
                  setHelperEntryError(null)
                  setIsTypingProblem(true)
                }}
                onTypedProblemChange={(value) => {
                  setTypedProblem(value)
                  if (helperEntryError) setHelperEntryError(null)
                }}
                onSubmitTyped={handleSubmitTypedProblem}
                onCancelTyped={() => {
                  setIsTypingProblem(false)
                  setHelperEntryError(null)
                }}
                onBack={() => {
                  setIsTypingProblem(false)
                  setTypedProblem('')
                  setHelperEntryError(null)
                  setExplainError(null)
                  setHelperIntent(null)
                }}
              />
            )}
          </div>
        </div>
      )
    }

    return (
      <div className="study-helper-desktop-new-question-layout">
        <div className="study-helper-desktop-new-question-container">
          {helperQuotaStatus}
          <div className="study-helper-desktop-question-workflow" data-report-exercise="true">
              <div className="study-helper-desktop-workflow-left">
                <div className="study-helper-desktop-question-card">
                  <div className="study-helper-desktop-card-header">
                    <span>Question</span>
                    {questionMetadata?.topic && (
                      <span className="study-helper-desktop-topic-tag">
                        {questionMetadata.topic}
                      </span>
                    )}
                    {analyzingQuestion && (
                      <span className="study-helper-desktop-analyzing-tag">Analyzing...</span>
                    )}
                  </div>
                  <div className="study-helper-desktop-card-content">
                    <MixedContentPreview
                      content={question.latex || question.text}
                      fontSize="1.5rem"
                    />
                  </div>
                  <button
                    className="study-helper-desktop-clear-btn-under-card"
                    onClick={handleClearAnswer}
                  >
                    Clear & Start Over
                  </button>
                </div>

                {answer && (
                  <div className="study-helper-desktop-answer-card">
                    <div className="study-helper-desktop-card-header">
                      Your Answer {attemptNumber > 1 && `(Attempt ${attemptNumber})`}
                    </div>
                    <div className="study-helper-desktop-card-content">
                      <MixedContentPreview
                        content={answer.latex || answer.text}
                        fontSize="1.5rem"
                      />
                    </div>
                  </div>
                )}
              </div>
              
              <div className="study-helper-desktop-workflow-right">
                {(processingImage || checkingAnswer) && (
                  <div className="study-room-processing-overlay">
                    <div className="study-room-bird-mascot thinking">
                      <img src="/favicon.png" alt="Quillnote bird thinking" />
                    </div>
                    <p className="study-room-processing-text">{processingImage ? 'Reading your work...' : 'Checking your answer...'}</p>
                  </div>
                )}
                {feedback && (
                  <div className={`study-helper-desktop-feedback-banner ${feedback.isCorrect ? 'correct' : 'incorrect'}`}>
                    {feedback.isCorrect && (
                      <div className="study-room-bird-mascot cheering">
                        <img src="/favicon.png" alt="Quillnote bird cheering" />
                      </div>
                    )}
                    <div className="study-helper-desktop-feedback-header">
                      {feedback.isCorrect ? '🎉 Correct!' : `Not Quite (Attempt ${attemptNumber} of 3)`}
                    </div>
                    <div className="study-helper-desktop-feedback-content">
                      {feedback.message}
                    </div>
                    {!feedback.isCorrect && (
                      <p className="study-room-encouragement-text">
                        {attemptNumber < 3
                          ? "Every attempt teaches you something new. You've got this!"
                          : "Learning is a journey. Each challenge helps you grow stronger."}
                      </p>
                    )}
                    {!feedback.isCorrect && aiChatHelperEnabled && (
                      <button
                        className="study-helper-desktop-ask-btn"
                        onClick={() => setShowAskModal(true)}
                      >
                        💬 Ask for Help
                      </button>
                    )}
                  </div>
                )}
                
                <div className="study-helper-desktop-workflow-actions">
                  {!feedback ? (
                    <>
                      <div className="study-helper-desktop-answer-upload-buttons">
                        <button
                          className="study-helper-desktop-action-btn upload-answer"
                          onClick={handleNewQuestionClick}
                          disabled={processingImage || checkingAnswer}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M4 16L4 17C4 18.6569 5.34315 20 7 20L17 20C18.6569 20 20 18.6569 20 17L20 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M16 8L12 4M12 4L8 8M12 4L12 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {processingImage ? 'Processing...' : checkingAnswer ? 'Checking...' : 'Upload Answer'}
                        </button>
                        <button
                          className="study-helper-desktop-action-btn camera-answer"
                          onClick={handleTakePhoto}
                          disabled={processingImage || checkingAnswer}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Take Photo
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      className={`study-helper-desktop-action-btn ${feedback.isCorrect || attemptNumber >= 3 ? 'save-continue' : 'try-again'}`}
                      onClick={feedback.isCorrect || attemptNumber >= 3 ? handleSave : handleTryAgain}
                      disabled={processingImage || checkingAnswer}
                    >
                      {processingImage ? 'Processing...' : checkingAnswer ? 'Checking...' : (
                        feedback.isCorrect || attemptNumber >= 3 ? 'Save & Continue' : 'Try Again'
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
        </div>
      </div>
    )
  }

  const renderDesktopQuestions = () => {
    if (loadingAvailableQuestions) {
      return <div className="study-helper-desktop-loading">Loading questions...</div>
    }

    const groups = []

    const parentQuestions = availableQuestions.filter(q => q.sourceType === 'parent-managed')
    if (parentQuestions.length > 0) {
      groups.push({ key: '__parent__', label: 'From Parent', questions: parentQuestions })
    }

    const textbookQuestions = availableQuestions.filter(q => q.sourceType === 'textbook')
    const textbookGroupMap = {}
    textbookQuestions.forEach(q => {
      const key = `${q.textbookTitle} - Page ${q.pageNumber}`
      if (!textbookGroupMap[key]) textbookGroupMap[key] = []
      textbookGroupMap[key].push(q)
    })
    Object.entries(textbookGroupMap).forEach(([label, questions]) => {
      groups.push({ key: label, label, questions })
    })

    const pageQuestions = availableQuestions.filter(q => q.sourceType === 'page')
    const pageGroupMap = {}
    pageQuestions.forEach(q => {
      const key = `page_${q.pageId}`
      if (!pageGroupMap[key]) pageGroupMap[key] = { label: `Page ${q.pageNumber}`, questions: [] }
      pageGroupMap[key].questions.push(q)
    })
    Object.entries(pageGroupMap).forEach(([key, { label, questions }]) => {
      groups.push({ key, label, questions })
    })

    if (groups.length === 0) {
      return (
        <div className="study-helper-desktop-empty-questions">
          <p>No questions available right now.</p>
          <p style={{ fontSize: 'calc(14px * var(--font-scale))', opacity: 0.7 }}>Your parent can assign questions for you to work on.</p>
        </div>
      )
    }

    const effectiveGroupKey = isMobile
      ? selectedQuestionsGroupKey
      : (selectedQuestionsGroupKey ?? groups[0]?.key)
    const selectedGroup = groups.find(g => g.key === effectiveGroupKey) || (!isMobile ? groups[0] : null)

    return (
      <div className={`study-helper-desktop-view-work-layout ${isMobile && selectedQuestionsGroupKey ? 'question-selected' : ''}`}>
        <div className="study-helper-desktop-questions-menu">
          <div className="study-helper-desktop-questions-menu-header">
            <div className="study-helper-desktop-questions-title">Questions</div>
          </div>
          <div className="study-helper-desktop-questions-list-scroll">
            {groups.map(group => {
              const isSelected = effectiveGroupKey === group.key
              const totalCount = group.questions.length
              const answeredCount = group.questions.filter(q => q.isAnswered).length
              return (
                <div
                  key={group.key}
                  className={`study-helper-desktop-question-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedQuestionsGroupKey(group.key)}
                >
                  <div className="study-helper-desktop-question-preview">{group.label}</div>
                  <ProgressBar answered={answeredCount} total={totalCount} />
                </div>
              )
            })}
          </div>
        </div>

        <div className="study-helper-desktop-answers-viewer">
          {selectedGroup ? (
            <div className="study-questions-page-detail">
              <div className="study-questions-page-detail-header">
                {isMobile && (
                  <button
                    className="study-helper-desktop-mobile-back-to-questions"
                    onClick={() => setSelectedQuestionsGroupKey(null)}
                  >
                    ← Back
                  </button>
                )}
                <div className="study-questions-page-detail-title">{selectedGroup.label}</div>
              </div>
              <div className="study-questions-page-detail-list">
                {selectedGroup.questions.map((q, idx) => (
                  <div
                    key={q.id}
                    className={`study-questions-page-question-card ${q.isAnswered ? 'answered' : 'unanswered'}`}
                    onClick={() => !q.isAnswered && handleSelectAvailableQuestion(q)}
                  >
                    <div className="study-questions-page-question-header">
                      <div className="study-questions-page-question-number">Q{idx + 1}</div>
                      {q.topic && (
                        <div className="study-questions-page-question-meta">
                          <span className="study-questions-page-question-tag">{q.topic}</span>
                        </div>
                      )}
                      <span className={`study-questions-question-status ${q.isAnswered ? 'answered' : 'unanswered'}`}>
                        {q.isAnswered ? '✓ Done' : 'Answer →'}
                      </span>
                    </div>
                    <div className="study-questions-page-question-text">
                      <MixedContentPreview content={q.question?.latex || q.question?.text || 'Question'} fontSize="15px" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="study-helper-desktop-placeholder-text">
              Select a group to view questions
            </div>
          )}
        </div>
      </div>
    )
  }

  const prizeState = competitionData?.prizeSelectionState
  const myRankEntry = prizeState?.rankings?.find(r => r.studentId === currentUser?.uid)
  const myRank = myRankEntry?.rank
  const myPrizeId = prizeState?.prizeChoices?.[currentUser?.uid]

  const isMyActiveTurn = prizeState && !myPrizeId && (
    (prizeState.currentTurn === 'rank1_first' && myRank === 1) ||
    (prizeState.currentTurn === 'rank2' && myRank === 2) ||
    (prizeState.currentTurn === 'rank1_second' && myRank === 1)
  )
  const windowClosesAt = prizeState?.windowClosesAt?.toDate ? prizeState.windowClosesAt.toDate() : null
  const windowOpen = windowClosesAt && new Date() < windowClosesAt
  const canChoosePrize = isMyActiveTurn && windowOpen

  const availablePrizes = (prizeState?.prizes || []).filter(p => !p.assignedTo)
  const chosenPrize = myPrizeId ? (prizeState?.prizes || []).find(p => p.id === myPrizeId) : null

  const handleChoosePrize = async (prizeId) => {
    if (!canChoosePrize || choosingPrize) return
    setChoosingPrize(true)
    setPrizeChooseError(null)
    try {
      const chooseFn = httpsCallable(functions, 'choosePrize')
      await chooseFn({ prizeId })
      const compSnap = await getDoc(doc(db, 'competition', 'spring2026'))
      if (compSnap.exists()) setCompetitionData(compSnap.data())
    } catch (e) {
      console.error('Error choosing prize:', e)
      setPrizeChooseError(e.message || 'Failed to choose prize. Please try again.')
    } finally {
      setChoosingPrize(false)
    }
  }

  useEffect(() => {
    if (!canChoosePrize || !windowClosesAt) {
      setPrizeCountdown('')
      return
    }
    const tick = () => {
      const diff = windowClosesAt.getTime() - Date.now()
      if (diff <= 0) {
        setPrizeCountdown('0s')
        return
      }
      const totalSec = Math.floor(diff / 1000)
      const d = Math.floor(totalSec / 86400)
      const h = Math.floor((totalSec % 86400) / 3600)
      const m = Math.floor((totalSec % 3600) / 60)
      const s = totalSec % 60
      if (d > 0) {
        setPrizeCountdown(`${d}d ${h}h ${m}m ${s}s`)
      } else if (h > 0) {
        setPrizeCountdown(`${h}h ${m}m ${s}s`)
      } else {
        setPrizeCountdown(`${m}m ${s}s`)
      }
    }
    tick()
    const timer = setInterval(tick, 1000)
    return () => clearInterval(timer)
  }, [canChoosePrize, windowClosesAt])

  const renderPrizeSelectionPanel = () => {
    if (!myRank || competitionData?.status !== 'ended') return null

    const rankMedal = PLACEMENT_MEDALS[myRank]
    const rankLabel = PLACEMENT_LABELS[myRank]

    return (
      <div className="study-room-prize-panel">
        <div className="study-room-prize-panel-header">
          <span className="study-room-prize-panel-medal">{rankMedal}</span>
          <span className="study-room-prize-panel-title">
            You placed <strong>{rankLabel}</strong> in the XP Competition!
          </span>
        </div>

        {chosenPrize && (
          <div className="study-room-prize-chosen-info">
            <img src={chosenPrize.imageUrl} alt={chosenPrize.name} className="study-room-prize-chosen-img" />
            <div>
              <div className="study-room-prize-chosen-label">Your prize:</div>
              <div className="study-room-prize-chosen-name">{chosenPrize.name}</div>
            </div>
          </div>
        )}

        {canChoosePrize && (
          <div className="study-room-prize-selection-area">
            <div className="study-room-prize-selection-prompt">
              Choose your prize!
              {prizeCountdown && (
                <span className="study-room-prize-window-close">
                  {' '}Time remaining: <strong>{prizeCountdown}</strong>
                </span>
              )}
            </div>
            {prizeChooseError && (
              <div className="study-room-prize-error">{prizeChooseError}</div>
            )}
            <div className="study-room-prizes-grid">
              {availablePrizes.map(prize => (
                <div key={prize.id} className="study-room-prize-card">
                  <img src={prize.imageUrl} alt={prize.name} className="study-room-prize-card-img" />
                  <div className="study-room-prize-card-name">{prize.name}</div>
                  <button
                    className="study-room-prize-choose-btn"
                    onClick={() => handleChoosePrize(prize.id)}
                    disabled={choosingPrize}
                  >
                    {choosingPrize ? 'Choosing...' : 'Choose This Prize'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!canChoosePrize && !chosenPrize && prizeState && prizeState.currentTurn !== 'done' && (
          <div className="study-room-prize-waiting">
            {isMyActiveTurn && !windowOpen
              ? 'Your selection window has closed.'
              : (
                <>
                  Your prize selection window hasn't opened yet.
                  {windowClosesAt && (
                    <span className="study-room-prize-opens-by">
                      {' '}The current winner's selection window closes on{' '}
                      <strong>{windowClosesAt.toLocaleString()}</strong>.
                      {' '}Your turn will open after that.
                    </span>
                  )}
                </>
              )}
          </div>
        )}

        {!canChoosePrize && !chosenPrize && prizeState?.currentTurn === 'done' && (
          <div className="study-room-prize-waiting">Prize selection is complete. Check your email for details!</div>
        )}
      </div>
    )
  }

    const reportExercise = buildExerciseContext({
      selectedMenuItem,
      explainSession,
      explainStepIndex,
      question,
      answer,
      feedback,
      questionMetadata,
      selectedAssignment,
      studyQuestionId: autoSaveDocIdRef.current,
      selectedViewWorkQuestion
    })

    const openReportModal = () => {
      if (reportSubmitting) return
      reportIdRef.current = createReportId()
      setReportCategory('')
      setReportMessage('')
      setReportIncludeScreenshot(false)
      setReportError(null)
      setReportSuccess(false)
      setReportFallbackNeeded(!document.querySelector('[data-report-exercise]'))
      setShowReportModal(true)
    }

    const closeReportModal = () => {
      if (reportSubmitting) return
      setShowReportModal(false)
    }

    const handleSubmitReport = async () => {
      if (reportSubmitting || !reportsEnabled) return
      const modal = document.querySelector('.student-report-modal')
      // Hide the dialog in the DOM before any React update, so the page
      // picture cannot include it and a re-render cannot put the name back
      // before the capture finishes.
      modal?.classList.add('student-report-modal--capturing')
      document.body.classList.add('student-report-capturing')
      setReportError(null)
      let restoreNames = () => {}
      let screenshot = null
      let exerciseImage = null
      try {
        restoreNames = await preparePageForCapture()
        if (reportScreencapEnabled && reportIncludeScreenshot) {
          try {
            screenshot = await captureViewportJpeg()
          } catch (captureError) {
            throw new Error(
              'We could not take the page picture. Turn it off and send the report, or try again.'
            )
          }
        }
        if (reportExercise) {
          try {
            exerciseImage = await captureExerciseJpeg()
          } catch (captureError) {
            exerciseImage = null
          }
        }
      } catch (error) {
        restoreNames()
        modal?.classList.remove('student-report-modal--capturing')
        document.body.classList.remove('student-report-capturing')
        setReportError(explainErrorMessage(
          error,
          'We could not send your report. Please try again.'
        ))
        return
      }
      restoreNames()
      modal?.classList.remove('student-report-modal--capturing')
      document.body.classList.remove('student-report-capturing')

      setReportSubmitting(true)
      try {
        await submitStudentReport({
          reportId: reportIdRef.current,
          ...actingStudentPayload,
          category: reportCategory,
          message: reportMessage,
          screenshot,
          exerciseImage,
          page: buildPageInfo(selectedMenuItem),
          exercise: reportExercise
        })
        setReportSuccess(true)
      } catch (error) {
        setReportError(explainErrorMessage(
          error,
          'We could not send your report. Please try again.'
        ))
      } finally {
        setReportSubmitting(false)
      }
    }

    const renderStudentReport = () => (
      <>
        {showReportModal && reportFallbackNeeded && reportExercise && (
          <ReportExerciseSheet
            exercise={reportExercise}
            explainSession={explainSession}
            explainStepIndex={explainStepIndex}
            groupedSolution={explainSolutionView === EXPLAIN_SOLUTION_VIEW_WORKSHEET}
          />
        )}
        {showReportModal && (
          <StudentReportModal
            reportsEnabled={reportsEnabled}
            screencapAllowed={reportScreencapEnabled}
            hasParent={Boolean(studentProfile?.parentId)}
            category={reportCategory}
            message={reportMessage}
            includeScreenshot={reportIncludeScreenshot}
            submitting={reportSubmitting}
            capturing={false}
            error={reportError}
            success={reportSuccess}
            onCategoryChange={setReportCategory}
            onMessageChange={setReportMessage}
            onScreenshotChange={setReportIncludeScreenshot}
            onClose={closeReportModal}
            onSubmit={handleSubmitReport}
          />
        )}
      </>
    )

    const renderMobileTopHeader = () => (
      <div className="study-mobile-top-header">
        <div className="study-mobile-header-brand">
          <img src='/logo.svg' alt='Quillnote' className='study-mobile-logo-icon' />
          <div className="study-helper-desktop-breadcrumb study-mobile-breadcrumb">
            <span className="study-helper-desktop-quillnote-text brand-text">Quillnote</span>
            <BetaVersionBadge size="sm" />
            {studentView && (
              <span className="study-room-student-view-badge" data-report-mask="student-name">
                {studentFirstName}
              </span>
            )}
          </div>
        </div>
        <div className="study-mobile-header-actions">
          <ThemeToggleButton />
          <button
            type="button"
            className="study-mobile-report-btn"
            onClick={openReportModal}
            aria-label="Report a problem"
          >
            <ReportProblemIcon style={{ fontSize: 22 }} />
          </button>
          <div className="study-mobile-more-wrap">
            <button
              className="study-mobile-more-btn"
              onClick={() => setShowMobileMoreMenu(v => !v)}
              aria-label="More options"
            >
              <MoreVertIcon style={{ fontSize: 22 }} />
            </button>
            {showMobileMoreMenu && (
              <>
                <div
                  className="study-mobile-more-backdrop"
                  onClick={() => setShowMobileMoreMenu(false)}
                />
                <div className="study-mobile-more-dropdown">
                  {studentView ? (
                    <>
                      <button
                        type="button"
                        className="study-mobile-more-item"
                        onClick={() => {
                          setShowMobileMoreMenu(false)
                          setTutorialManual(true)
                        }}
                      >
                        Tutorial
                      </button>
                      <button
                        className="study-mobile-more-item"
                        onClick={() => { setShowMobileMoreMenu(false); onExitStudentView?.() }}
                      >
                        Exit Student View
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="study-mobile-more-item"
                        onClick={() => {
                          setShowMobileMoreMenu(false)
                          setTutorialManual(true)
                        }}
                      >
                        Tutorial
                      </button>
                      {userProfile?.accountOrigin === 'selfSignup' &&
                        userProfile?.role === 'student' &&
                        !userProfile?.parentId &&
                        userProfile?.pendingStudentUsername !== true && (
                        <button
                          type='button'
                          className='study-mobile-more-item'
                          onClick={() => {
                            setShowMobileMoreMenu(false)
                            setShowParentInvite(true)
                          }}
                        >
                          Invite a parent
                        </button>
                      )}
                      <button
                        className="study-mobile-more-item study-mobile-more-item-danger"
                        onClick={() => { handleLogout(); setShowMobileMoreMenu(false) }}
                      >
                        Logout
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    )

    const renderMobileBottomNav = () => (
      <div className="study-mobile-bottom-nav">
        <button
          className={`study-mobile-tab-btn ${selectedMenuItem === 'questions' ? 'active' : ''}`}
          onClick={() => setSelectedMenuItem('questions')}
        >
          <ListIcon style={{ fontSize: 22 }} />
          <span>Questions</span>
        </button>
        <button
          className={`study-mobile-tab-btn ${selectedMenuItem === 'helper' ? 'active' : ''}`}
          onClick={() => setSelectedMenuItem('helper')}
        >
          <EditIcon style={{ fontSize: 22 }} />
          <span>Helper</span>
        </button>
        <button
          className={`study-mobile-tab-btn ${selectedMenuItem === 'view-work' ? 'active' : ''}`}
          onClick={() => setSelectedMenuItem('view-work')}
        >
          <HistoryIcon style={{ fontSize: 22 }} />
          <span>View Work</span>
        </button>
      </div>
    )

    const usernameGate = !studentView && userProfile?.pendingStudentUsername === true
      ? <StudentUsernameModal onComplete={refreshUserProfile} />
      : null

    if (isMobile) {
      return (
        <div className={`study-helper-desktop dynamic-bg${isCelebrating ? ' dynamic-bg--celebrating' : ''}`}>
          {renderMobileTopHeader()}
          <div className="study-mobile-content-area">
            {!(
              selectedMenuItem === 'helper'
              && helperIntent === 'explain'
              && !explainFinishModalOpen
              && (Boolean(explainSession) || processingImage)
            ) && (
              <GamificationBar xp={xp} streak={streak} badges={badges} placementRank={myRank} />
            )}
            {renderPrizeSelectionPanel()}
            {selectedMenuItem === 'questions' && renderDesktopQuestions()}
            {selectedMenuItem === 'helper' && renderHelperTab()}
            {selectedMenuItem === 'view-work' && renderDesktopQuestionCards()}
          </div>
          {renderExplainPlayerDock()}
          {renderMobileBottomNav()}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
          />
          {showImageCrop && imageToCrop && (
            <ImageCrop
              key={imageToCrop}
              imageSrc={imageToCrop}
              onCropComplete={handleCropComplete}
              onCancel={handleCropCancel}
            />
          )}
          {showAskModal && feedback && question && answer && (
            <AskModal
              onClose={() => setShowAskModal(false)}
              feedbackMessage={feedback.message}
              question={question.latex || question.text}
              studentAnswer={answer.latex || answer.text}
              onMessagesUpdate={handleChatMessagesUpdate}
              quota={helperQuotaSource()}
              onQuotaExceeded={() => setShowHelperQuotaModal(true)}
            />
          )}
          {showExplainAskModal && explainSession && (
            <ExplainAskModal
              sessionId={explainSession.sessionId}
              stepIndex={explainStepIndex}
              messages={explainChatMessages}
              actingStudentId={studentView ? actingStudentId : null}
              onClose={() => setShowExplainAskModal(false)}
              onMessagesAppend={handleExplainMessagesAppend}
            />
          )}
          {explainFinishModalOpen && (
            <ExplainFinishModal
              xpAwarded={explainCompletion.xpAwarded}
              xpAmount={explainCompletion.xpAmount}
              onHide={handleHideExplainFinishModal}
              onExplainAnother={handleExplainAnother}
              onSwitchToCheckAnswer={handleExplainSwitchToCheckAnswer}
            />
          )}
          <CelebrationOverlay showConfetti={showConfetti} showXpPop={showXpPop} xpAmount={xpEarned} />
          {renderStudentReport()}
          {showCameraModal && (
            <CameraCaptureModal
              cameraError={cameraError}
              cameraStream={cameraStream}
              videoRef={videoRef}
              canvasRef={canvasRef}
              onClose={handleCloseCamera}
              onRetry={handleOpenCamera}
              onCapture={handleCapturePhoto}
            />
          )}
          {showParentInvite &&
            userProfile?.pendingStudentUsername !== true && (
            <div className='helper-quota-modal-overlay' onClick={() => setShowParentInvite(false)}>
              <div
                className='helper-quota-modal'
                role='dialog'
                aria-modal='true'
                aria-labelledby='parent-invite-dialog-title'
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type='button'
                  className='helper-quota-modal-close'
                  onClick={() => setShowParentInvite(false)}
                  aria-label='Close'
                >
                  ×
                </button>
                <ParentInvitePanel />
              </div>
            </div>
          )}
          {showStudentTutorial && (
            <StudyHelperDemo
              audience="student"
              onClose={closeStudentTutorial}
              onComplete={persistStudentTutorial}
            />
          )}
          {usernameGate}
          {showHelperQuotaModal && (
            <HelperQuotaModal onClose={() => setShowHelperQuotaModal(false)}>
              {userProfile?.accountOrigin === 'selfSignup' &&
                !userProfile?.parentId &&
                userProfile?.pendingStudentUsername !== true && (
                <ParentInvitePanel />
              )}
            </HelperQuotaModal>
          )}
        </div>
      )
    }

    return (
      <div className={`study-helper-desktop dynamic-bg${isCelebrating ? ' dynamic-bg--celebrating' : ''}`}>
        <div className="study-helper-desktop-main-content">
          <div className="study-helper-desktop-menu-bar">
            <div className="study-helper-desktop-left-section">
              <img src={logoSrc} alt="Quillnote" className="study-helper-desktop-logo" />
              <div className="study-helper-desktop-breadcrumb">
                <BetaVersionBadge size="md" />
                <span className="study-helper-desktop-current-student">
                  <span data-report-mask="student-name">{studentFirstName}</span>
                  's Study Helper
                </span>
                {studentView && (
                  <span className="study-room-student-view-badge">Student View</span>
                )}
              </div>
            </div>
            
            <div className="study-helper-desktop-center-section">
              <div className="study-helper-desktop-menu-buttons">
                <button
                  className={`study-helper-desktop-menu-item ${selectedMenuItem === 'questions' ? 'selected' : ''}`}
                  onClick={() => setSelectedMenuItem('questions')}
                >
                  Questions
                </button>
                <button
                  className={`study-helper-desktop-menu-item ${selectedMenuItem === 'helper' ? 'selected' : ''}`}
                  onClick={() => setSelectedMenuItem('helper')}
                >
                  Helper
                </button>
                <button
                  className={`study-helper-desktop-menu-item ${selectedMenuItem === 'view-work' ? 'selected' : ''}`}
                  onClick={() => setSelectedMenuItem('view-work')}
                >
                  View Work
                </button>
                <button
                  type="button"
                  className="study-helper-desktop-menu-item"
                  onClick={() => setTutorialManual(true)}
                >
                  Tutorial
                </button>
                <button
                  type="button"
                  className="study-helper-desktop-menu-item"
                  onClick={openReportModal}
                >
                  Report Feedback/Issue
                </button>
                {userProfile?.accountOrigin === 'selfSignup' &&
                  userProfile?.role === 'student' &&
                  !userProfile?.parentId &&
                  !studentView &&
                  userProfile?.pendingStudentUsername !== true && (
                  <button
                    type="button"
                    className="study-helper-desktop-menu-item"
                    onClick={() => setShowParentInvite(true)}
                  >
                    Invite a parent
                  </button>
                )}
              </div>
            </div>

            <div className="study-helper-desktop-right-section">
              <ThemeToggleButton />
              {studentView ? (
                <button
                  className="study-helper-desktop-logout-btn"
                  onClick={() => onExitStudentView?.()}
                >
                  Exit Student View
                </button>
              ) : (
                <button className="study-helper-desktop-logout-btn" onClick={handleLogout}>
                  Logout
                </button>
              )}
            </div>
          </div>

          <GamificationBar xp={xp} streak={streak} badges={badges} placementRank={myRank} />
          {renderPrizeSelectionPanel()}
          
          {selectedMenuItem === 'questions' && renderDesktopQuestions()}
          {selectedMenuItem === 'helper' && renderHelperTab()}
          {selectedMenuItem === 'view-work' && renderDesktopQuestionCards()}
          {renderExplainPlayerDock()}
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture={isMobile ? "camera" : undefined}
          onChange={handlePhotoUpload}
          style={{ display: 'none' }}
        />

        {showImageCrop && imageToCrop && (
          <ImageCrop
            key={imageToCrop}
            imageSrc={imageToCrop}
            onCropComplete={handleCropComplete}
            onCancel={handleCropCancel}
          />
        )}

        {showAskModal && feedback && question && answer && (
          <AskModal
            onClose={() => setShowAskModal(false)}
            feedbackMessage={feedback.message}
            question={question.latex || question.text}
            studentAnswer={answer.latex || answer.text}
            onMessagesUpdate={handleChatMessagesUpdate}
            quota={helperQuotaSource()}
            onQuotaExceeded={() => setShowHelperQuotaModal(true)}
          />
        )}

        {showExplainAskModal && explainSession && (
          <ExplainAskModal
            sessionId={explainSession.sessionId}
            stepIndex={explainStepIndex}
            messages={explainChatMessages}
            actingStudentId={studentView ? actingStudentId : null}
            onClose={() => setShowExplainAskModal(false)}
            onMessagesAppend={handleExplainMessagesAppend}
          />
        )}

        {explainFinishModalOpen && (
          <ExplainFinishModal
            xpAwarded={explainCompletion.xpAwarded}
            xpAmount={explainCompletion.xpAmount}
            onHide={handleHideExplainFinishModal}
            onExplainAnother={handleExplainAnother}
            onSwitchToCheckAnswer={handleExplainSwitchToCheckAnswer}
          />
        )}

        <CelebrationOverlay showConfetti={showConfetti} showXpPop={showXpPop} xpAmount={xpEarned} />
        {renderStudentReport()}

        {showCameraModal && (
          <CameraCaptureModal
            cameraError={cameraError}
            cameraStream={cameraStream}
            videoRef={videoRef}
            canvasRef={canvasRef}
            onClose={handleCloseCamera}
            onRetry={handleOpenCamera}
          onCapture={handleCapturePhoto}
        />
        )}
        {showParentInvite &&
          userProfile?.pendingStudentUsername !== true && (
          <div className='helper-quota-modal-overlay' onClick={() => setShowParentInvite(false)}>
            <div
              className='helper-quota-modal'
              role='dialog'
              aria-modal='true'
              aria-labelledby='parent-invite-dialog-title'
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type='button'
                className='helper-quota-modal-close'
                onClick={() => setShowParentInvite(false)}
                aria-label='Close'
              >
                ×
              </button>
              <ParentInvitePanel />
            </div>
          </div>
        )}
        {showStudentTutorial && (
          <StudyHelperDemo
            audience="student"
            onClose={closeStudentTutorial}
            onComplete={persistStudentTutorial}
          />
        )}
        {usernameGate}
        {showHelperQuotaModal && (
          <HelperQuotaModal onClose={() => setShowHelperQuotaModal(false)}>
            {userProfile?.accountOrigin === 'selfSignup' &&
              !userProfile?.parentId &&
              userProfile?.pendingStudentUsername !== true && (
              <ParentInvitePanel />
            )}
          </HelperQuotaModal>
        )}
      </div>
    )
}

export default StudyRoom
